import * as XLSX from "xlsx/xlsx.mjs";
import JSZip from "jszip";

const uid = () => crypto.randomUUID();

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Resolve an OPC relationship Target to a path inside the zip.
//
// Excel writes these relative to xl/ ("worksheets/sheet1.xml"), but the spec also
// allows an absolute package path, and openpyxl uses it ("/xl/worksheets/sheet1.xml").
// Treating an absolute target as already-resolved left the leading slash on, so
// zip.file() returned null and every colored cell in the workbook was silently
// ignored — the import fell back to structural guessing with no error anywhere.
// Any writer that is not Excel is liable to do this: openpyxl, LibreOffice, exports
// out of Google Sheets.
function normalizeRelTarget(target) {
  if (!target) return "";
  if (target.startsWith("http")) return target;
  if (target.startsWith("/")) return target.replace(/^\/+/, "");
  return "xl/" + target.replace(/^\.\.\//, "");
}

function isSectionHeader(v) {
  if (!v || typeof v !== "string") return false;
  const t = v.trim();
  if (t.length < 3 || t.length > 80) return false;
  if (t !== t.toUpperCase()) return false;
  if (!/[A-Z]/.test(t)) return false;
  if (t.endsWith(":")) return false;
  // Exclude alphanumeric codes like "T2130X34" or "X2890" — real section headers
  // are either purely alphabetic or contain spaces (e.g. "SAMPLE INFORMATION")
  if (/\d/.test(t) && !/\s/.test(t)) return false;
  return true;
}

// Mixed-case title row: a single non-colon string cell in a row where all other cells are blank
// e.g. "Liquid Limit - Tex-104-E"
function isTitleRow(G, r) {
  const row = G[r] || [];
  const filled = row.filter(c => c && c.v !== undefined && c.v !== null && c.v !== "");
  if (filled.length !== 1) return false;
  const v = String(filled[0].v).trim();
  // Must be a reasonable section title: mixed-case, no colons, not too long
  return v.length >= 4 && v.length <= 60 && !v.endsWith(":") && !v.endsWith("?") && !v.includes(":") && typeof filled[0].v === "string";
}

function isLabel(v) {
  if (!v || typeof v !== "string") return false;
  const t = v.trim();
  return t.endsWith(":") && t.length > 1 && !t.startsWith("=");
}

function isFormulaVal(cell) {
  if (!cell) return false;
  if (cell.f) return true;
  if (typeof cell.v === "string" && cell.v.trim().startsWith("=")) return true;
  return false;
}

function cellVal(cell) {
  if (!cell) return null;
  return cell.v !== undefined ? cell.v : null;
}

// ─── Theme color resolver ─────────────────────────────────────────────────────
// Approximated Office default theme palette. Applies OOXML tint formula.
const THEME_BASE_COLORS = [
  "#FFFFFF","#000000","#1F3864","#EEECE1","#4472C4","#ED7D31",
  "#A9D18E","#FF0000","#FFC000","#70AD47",
];
function resolveThemeColor(themeIdx, tint) {
  const hex = THEME_BASE_COLORS[themeIdx] || "#808080";
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  const t = tint || 0;
  const apply = (c) => t > 0 ? Math.round(c + (255-c)*t) : Math.round(c*(1+t));
  const rr = apply(r).toString(16).padStart(2,"0");
  const gg = apply(g).toString(16).padStart(2,"0");
  const bb = apply(b).toString(16).padStart(2,"0");
  return `#${rr}${gg}${bb}`;
}

// Returns Map<rowIndex(0-based), bgHex> for rows that have a row-level fill style.
// SheetJS does not inherit row-level fills to cells with no cell-level style, so we must
// read them directly from the XML and apply them as fallback backgrounds in the editor.
async function getRowFillMap(arrayBuffer, sheetName) {
  const result = new Map();
  try {
    const zip = await JSZip.loadAsync(arrayBuffer);
    // Build sheetName → file path
    const wbXml = await zip.file("xl/workbook.xml")?.async("string");
    const relsXml = await zip.file("xl/_rels/workbook.xml.rels")?.async("string");
    if (!wbXml || !relsXml) return result;
    const parser = new DOMParser();
    const wbDoc = parser.parseFromString(wbXml, "application/xml");
    const relsDoc = parser.parseFromString(relsXml, "application/xml");
    const rIdToPath = new Map();
    for (const rel of relsDoc.getElementsByTagName("Relationship")) {
      const target = normalizeRelTarget(rel.getAttribute("Target") || "");
      if (rel.getAttribute("Id")) rIdToPath.set(rel.getAttribute("Id"), target);
    }
    const REL_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
    let sheetPath = null;
    for (const s of wbDoc.getElementsByTagName("sheet")) {
      if (s.getAttribute("name") === sheetName) {
        const rId = s.getAttribute("r:id") || s.getAttributeNS(REL_NS, "id");
        sheetPath = rIdToPath.get(rId) || null;
        break;
      }
    }
    if (!sheetPath) return result;

    // Parse styles to build xfIndex → bgHex
    const stylesXml = await zip.file("xl/styles.xml")?.async("string");
    if (!stylesXml) return result;
    const stylesDoc = parser.parseFromString(stylesXml, "application/xml");
    const fillEls = stylesDoc.getElementsByTagName("fill");
    const fillIdToHex = new Map();
    for (let i = 0; i < fillEls.length; i++) {
      const pf = fillEls[i].getElementsByTagName("patternFill")[0];
      if (!pf || pf.getAttribute("patternType") === "none" || pf.getAttribute("patternType") === "gray125") continue;
      const fg = pf.getElementsByTagName("fgColor")[0];
      if (!fg) continue;
      const rgb = fg.getAttribute("rgb");
      if (rgb) {
        const hex8 = rgb.toUpperCase().length === 6 ? "FF" + rgb.toUpperCase() : rgb.toUpperCase();
        if (hex8 !== "FFFFFFFF" && !hex8.startsWith("00")) fillIdToHex.set(i, "#" + hex8.slice(2));
      }
    }
    const xfEls = (stylesDoc.getElementsByTagName("cellXfs")[0] || { getElementsByTagName: () => [] }).getElementsByTagName("xf");
    const xfToHex = new Map();
    for (let i = 0; i < xfEls.length; i++) {
      const fillId = parseInt(xfEls[i].getAttribute("fillId") || "0", 10);
      if (fillIdToHex.has(fillId)) xfToHex.set(i, fillIdToHex.get(fillId));
    }

    // Parse sheet XML — find <row> elements with s attribute and customFormat="1"
    const sheetXml = await zip.file(sheetPath)?.async("string");
    if (!sheetXml) return result;
    const sheetDoc = parser.parseFromString(sheetXml, "application/xml");
    for (const row of sheetDoc.getElementsByTagName("row")) {
      if (row.getAttribute("customFormat") !== "1") continue;
      const s = parseInt(row.getAttribute("s") || "-1", 10);
      if (s < 0) continue;
      const hex = xfToHex.get(s);
      if (!hex) continue;
      const r = parseInt(row.getAttribute("r") || "0", 10) - 1; // convert to 0-based
      result.set(r, hex);
    }
  } catch (e) { console.warn("getRowFillMap:", e); }
  return result;
}

// Returns the set of cell addresses that had a yellow (#FFFBCC) input fill in the original template.
// Used to detect "data rows" — rows that should only appear when the user has entered a value.
// Returns Set of all input cell addresses (yellow = optional, red = required)
function getInputCellSet(rawBytes, sheetName) {
  try {
    const wb = XLSX.read(rawBytes, { type: "array", cellStyles: true });
    const ws = wb.Sheets[sheetName] || wb.Sheets[wb.SheetNames[0]];
    const set = new Set();
    if (!ws) return set;
    for (const key of Object.keys(ws)) {
      if (key.startsWith("!")) continue;
      const cell = ws[key];
      const rgb = cell?.s?.fgColor?.rgb;
      if (!rgb) continue;
      const hex6 = rgb.toUpperCase().slice(-6);
      if (hex6 === "FFFBCC" || isRedFill(hex6)) set.add(key);
    }
    return set;
  } catch { return new Set(); }
}

// Returns true if a hex color string is dark enough to warrant white text
function isDarkHex(hex) {
  if (!hex) return false;
  const h = hex.replace("#", "").slice(-6);
  if (h.length < 6) return false;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.45;
}

// True if a fill color is red or reddish (hue 0–25° or 335–360°, red dominant)
// Used to detect required-field cells in Excel templates
function isRedFill(hex) {
  if (!hex) return false;
  const h = hex.replace("#", "").slice(-6);
  if (h.length < 6) return false;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max < 0.3) return false;          // too dark (near black)
  if (max - min < 0.08) return false;   // too desaturated (grey/white)
  if (max !== r) return false;          // red must be the dominant channel
  const delta = max - min;
  const hue = ((g - b) / delta % 6 + 6) % 6 * 60;
  return hue <= 25 || hue >= 335;       // red hue range
}

// Stricter than isDarkHex — only truly black or near-black fills (luminance < 0.20)
// Used for section bar detection so dark greys/blues don't accidentally trigger section breaks
function isNearBlack(hex) {
  if (!hex) return false;
  const h = hex.replace("#", "").slice(-6);
  if (h.length < 6) return false;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.20;
}

// ─── JSZip-based color detection ─────────────────────────────────────────────
// Returns Map<sheetName, { inputs: Set<cellAddr>, required: Set<cellAddr> }>
// inputs = all highlighted cells (yellow optional + red required)
// required = red-fill cells only (required fields)
async function getColoredCellMap(arrayBuffer) {
  let zip;
  try {
    zip = await JSZip.loadAsync(arrayBuffer);
  } catch (e) {
    return new Map();
  }

  // 1. Parse xl/styles.xml → find fill indices that have a visible (non-white) color
  const stylesFile = zip.file("xl/styles.xml");
  if (!stylesFile) return new Map();
  const stylesXml = await stylesFile.async("string");
  const parser = new DOMParser();
  const stylesDoc = parser.parseFromString(stylesXml, "application/xml");

  // Per the template guide, ONLY yellow (#FFFBCC) = optional input and red = required input.
  // Any other fill (grey, blue, tan, etc.) is a column header or section bar — not an input.
  // Detecting all non-white fills caused grey column header rows to be treated as input fields.
  const fillEls = stylesDoc.getElementsByTagName("fill");
  const highlightedFillIds = new Set();
  const requiredFillIds = new Set(); // red fills only
  for (let i = 0; i < fillEls.length; i++) {
    const patternFill = fillEls[i].getElementsByTagName("patternFill")[0];
    if (!patternFill) continue;
    const patternType = patternFill.getAttribute("patternType");
    if (!patternType || patternType === "none" || patternType === "gray125") continue;

    const fgColor = patternFill.getElementsByTagName("fgColor")[0];
    if (!fgColor) continue;

    const rgb = fgColor.getAttribute("rgb");
    if (!rgb) continue; // theme/indexed fills are never yellow or red input fills

    const upper = rgb.toUpperCase();
    const hex8 = upper.length === 6 ? "FF" + upper : upper;
    if (hex8.startsWith("00")) continue; // transparent
    const hex6 = hex8.slice(2);

    // Yellow: #FFFBCC (with minor tolerance for rounding)
    const rv = parseInt(hex6.slice(0, 2), 16);
    const gv = parseInt(hex6.slice(2, 4), 16);
    const bv = parseInt(hex6.slice(4, 6), 16);
    const isYellow = rv >= 245 && gv >= 240 && bv <= 220 && bv >= 160;

    if (isYellow) {
      highlightedFillIds.add(i);
    } else if (isRedFill(hex6)) {
      highlightedFillIds.add(i);
      requiredFillIds.add(i);
    }
  }

  if (highlightedFillIds.size === 0) return new Map();

  // 2. Parse cellXfs → build map: xfIndex → highlighted/required
  const cellXfs = stylesDoc.getElementsByTagName("cellXfs")[0];
  const xfEls = cellXfs ? cellXfs.getElementsByTagName("xf") : [];
  const highlightedXfIds = new Set();
  const requiredXfIds = new Set();
  for (let i = 0; i < xfEls.length; i++) {
    const fillId = parseInt(xfEls[i].getAttribute("fillId") || "0", 10);
    if (highlightedFillIds.has(fillId)) {
      highlightedXfIds.add(i);
      if (requiredFillIds.has(fillId)) requiredXfIds.add(i);
    }
  }

  if (highlightedXfIds.size === 0) return new Map();

  // 3. Build sheetName → file path via workbook.xml + workbook.xml.rels
  const wbFile = zip.file("xl/workbook.xml");
  const relsFile = zip.file("xl/_rels/workbook.xml.rels");
  if (!wbFile || !relsFile) return new Map();

  const wbDoc = parser.parseFromString(await wbFile.async("string"), "application/xml");
  const relsDoc = parser.parseFromString(await relsFile.async("string"), "application/xml");

  // rId → sheet file path (relative to xl/)
  const rIdToPath = new Map();
  const relEls = relsDoc.getElementsByTagName("Relationship");
  for (let i = 0; i < relEls.length; i++) {
    const id = relEls[i].getAttribute("Id");
    const target = normalizeRelTarget(relEls[i].getAttribute("Target") || "");
    if (id) rIdToPath.set(id, target);
  }

  const sheetNameToPath = new Map();
  const sheetEls = wbDoc.getElementsByTagName("sheet");
  const REL_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
  for (let i = 0; i < sheetEls.length; i++) {
    const name = sheetEls[i].getAttribute("name");
    const rId = sheetEls[i].getAttribute("r:id") || sheetEls[i].getAttributeNS(REL_NS, "id");
    if (name && rId && rIdToPath.has(rId)) {
      sheetNameToPath.set(name, rIdToPath.get(rId));
    }
  }

  // 4. Parse each sheet XML → collect addresses of highlighted cells (split by type)
  const result = new Map();
  for (const [sheetName, path] of sheetNameToPath) {
    const sheetFile = zip.file(path);
    if (!sheetFile) continue;
    const sheetXml = await sheetFile.async("string");
    const sheetDoc = parser.parseFromString(sheetXml, "application/xml");
    const cellEls = sheetDoc.getElementsByTagName("c");
    const inputs = new Set();
    const required = new Set();
    for (let i = 0; i < cellEls.length; i++) {
      const addr = cellEls[i].getAttribute("r");
      const s = cellEls[i].getAttribute("s");
      if (!addr || s === null) continue;
      const xfId = parseInt(s, 10);
      if (highlightedXfIds.has(xfId)) {
        inputs.add(addr);
        if (requiredXfIds.has(xfId)) required.add(addr);
      }
    }
    if (inputs.size > 0) result.set(sheetName, { inputs, required });
  }

  return result;
}

// ─── Parse ONE sheet ──────────────────────────────────────────────────────────
// coloredAddrs: Set<cellAddr> of all input cells; requiredAddrs: Set<cellAddr> of red (required) cells
// ─── Block segmentation ───────────────────────────────────────────────────────
// A sheet is not one flat table. People put blocks beside each other — a reference
// panel next to a header, two short tables sharing a row band — and a label lookup
// that scans a fixed distance up or left walks straight out of one block and into
// its neighbour. That is how a title in column N came to name the fields in column
// A, and how a table's column header reached a block eleven rows below it.
//
// Blocks are found by splitting on whitespace, the way a person reads the page:
// into row bands separated by fully blank rows, then each band into column bands
// separated by columns that are blank across that band.
function computeBlocks(G, maxRow, maxCol, isInput) {
  // An empty input cell is content. A blank yellow cell waiting to be filled in has
  // no value, and treating it as whitespace splits its block away from its own label
  // or drops it outside every block, where the scans run unbounded again.
  const filled = (r, c) => {
    if (isInput && isInput(r, c)) return true;
    const cell = G[r]?.[c];
    return !!(cell && cell.v !== undefined && cell.v !== null && cell.v !== "");
  };
  const rowHasContent = (r) => {
    for (let c = 0; c <= maxCol; c++) if (filled(r, c)) return true;
    return false;
  };

  const blocks = [];
  let r = 0;
  while (r <= maxRow) {
    if (!rowHasContent(r)) { r++; continue; }
    let r1 = r;
    while (r1 + 1 <= maxRow && rowHasContent(r1 + 1)) r1++;

    const colHasContent = (c) => {
      for (let rr = r; rr <= r1; rr++) if (filled(rr, c)) return true;
      return false;
    };
    let c = 0;
    while (c <= maxCol) {
      if (!colHasContent(c)) { c++; continue; }
      let c1 = c;
      while (c1 + 1 <= maxCol && colHasContent(c1 + 1)) c1++;
      blocks.push({ r0: r, r1, c0: c, c1 });
      c = c1 + 1;
    }
    r = r1 + 1;
  }
  return blocks;
}

// The single text value on row r within block b, or null when the row holds more
// than one value, a number, or nothing. A lone string on its own row is how a
// person titles a block. A trailing colon is stripped, so a title can carry one
// to keep itself out of isSectionHeader without the colon reaching the section name.
// isInput(r, c) marks a highlighted input cell. A row holding one is a label-and-
// value row, not a title, even when the input is still blank and the label is
// therefore the only value on it.
function loneTextInBlock(G, r, b, isInput) {
  let found = null;
  for (let c = b.c0; c <= b.c1; c++) {
    if (isInput && isInput(r, c)) return null;
    const cell = G[r]?.[c];
    if (!cell || cell.v === undefined || cell.v === null || cell.v === "") continue;
    if (found !== null || typeof cell.v !== "string") return null;
    found = cell.v.trim();
  }
  if (!found) return null;
  const t = found.replace(/:$/, "").trim();
  return t.length >= 3 && t.length <= 80 && !t.includes(":") ? t : null;
}

export function parseSheetToTemplate(wb, sheetName, coloredAddrs, requiredAddrs = null) {
  const ws = wb.Sheets[sheetName];
  if (!ws || !ws["!ref"]) return null;

  const range = XLSX.utils.decode_range(ws["!ref"]);
  const maxRow = range.e.r;
  const maxCol = range.e.c;

  // Build grid
  const G = [];
  for (let r = 0; r <= maxRow; r++) {
    G[r] = [];
    for (let c = 0; c <= maxCol; c++) {
      G[r][c] = ws[XLSX.utils.encode_cell({ r, c })] || null;
    }
  }

  function get(r, c) { return G[r]?.[c] || null; }

  // Every label and section lookup below is bounded to the block its cell sits in,
  // so a scan stops at the edge of the block instead of running a fixed number of
  // cells into whatever happens to be next to it.
  const isInputCell = (r, c) =>
    !!coloredAddrs && coloredAddrs.has(XLSX.utils.encode_cell({ r, c }));

  // A merged cell holds its value only in the top-left, but it occupies the whole
  // range on the page. A title bar merged across A:F visually heads everything
  // under A through F, so for block purposes it fills all of them. Without this a
  // label/value pair further right sits in a block of its own that cannot see the
  // bar titling it.
  const mergeCovered = new Set();
  (ws["!merges"] || []).forEach(m => {
    const tl = G[m.s.r]?.[m.s.c];
    if (!tl || tl.v === undefined || tl.v === null || tl.v === "") return;
    for (let mr = m.s.r; mr <= m.e.r; mr++)
      for (let mc = m.s.c; mc <= m.e.c; mc++) mergeCovered.add(`${mr},${mc}`);
  });
  // A section bar is painted across the sheet but only its first cell holds text.
  // On the page it spans everything under it, so it occupies its full width here
  // too — otherwise a label/value pair further right forms its own block and cannot
  // see the bar heading it.
  const isBarCell = (r, c) => {
    const rgb = G[r]?.[c]?.s?.fgColor?.rgb;
    return !!rgb && isNearBlack(String(rgb).slice(-6));
  };
  const occupies = (r, c) =>
    isInputCell(r, c) || mergeCovered.has(`${r},${c}`) || isBarCell(r, c);

  const blocks = computeBlocks(G, maxRow, maxCol, occupies);
  const blockFor = (r, c) =>
    blocks.find(b => r >= b.r0 && r <= b.r1 && c >= b.c0 && c <= b.c1)
    || { r0: 0, r1: maxRow, c0: 0, c1: maxCol };

  // ── PASS 0: Highlighted-cell detection ────────────────────────────────────
  // Uses JSZip-parsed coloredAddrs when available, otherwise skips (no SheetJS fallback)

  // Build merge map: cell addr → top-left addr of its merge region
  const addrToMergeTL = new Map();
  if (ws["!merges"]) {
    ws["!merges"].forEach(merge => {
      const tl = XLSX.utils.encode_cell(merge.s);
      for (let mr = merge.s.r; mr <= merge.e.r; mr++) {
        for (let mc = merge.s.c; mc <= merge.e.c; mc++) {
          addrToMergeTL.set(XLSX.utils.encode_cell({ r: mr, c: mc }), tl);
        }
      }
    });
  }

  const highlightInputs = [];

  if (coloredAddrs && coloredAddrs.size > 0) {
    // Sort addresses by row then column for deterministic ordering
    const sortedAddrs = [...coloredAddrs].sort((a, b) => {
      const da = XLSX.utils.decode_cell(a), db = XLSX.utils.decode_cell(b);
      return da.r !== db.r ? da.r - db.r : da.c - db.c;
    });

    const seenMergeTLs = new Set();

    for (const addr of sortedAddrs) {
      const { r, c } = XLSX.utils.decode_cell(addr);
      if (r > maxRow || c > maxCol) continue;

      // Merge deduplication: only process the first highlighted cell found per merge region
      const mergeTL = addrToMergeTL.get(addr);
      if (mergeTL) {
        // Skip cells whose merge TL is not itself highlighted (e.g. MATERIAL NAME where D15
        // is the TL but only E15+ are colored — indicates a freeform label cell, not input)
        if (mergeTL !== addr && !coloredAddrs.has(mergeTL)) continue;
        if (seenMergeTLs.has(mergeTL)) continue;
        seenMergeTLs.add(mergeTL);
      }

      const cell = get(r, c);
      // Formula cells are skipped unless they are yellow-highlighted (user explicitly marked as input)
      if (cell?.f && !coloredAddrs.has(addr)) continue;

      // Skip cells whose own text looks like a header or label
      if (cell && typeof cell.v === "string") {
        const v = cell.v.trim();
        if (isSectionHeader(v) || isLabel(v)) continue;
      }

      const blk = blockFor(r, c);

      // Find row label: scan left for the nearest text (colon-label or plain identifier)
      let rowLabel = null;
      let rowLabelFromColon = false;
      // Scan to the block edge. The old fixed 10-cell window was a guess at where
      // a block ended; now that blocks are known, the block edge is the answer.
      for (let lc = c - 1; lc >= blk.c0; lc--) {
        const lcel = get(r, lc);
        if (!lcel) continue;
        if (typeof lcel.v === "string" && lcel.v.trim().length > 1) {
          rowLabel = lcel.v.trim().replace(/:$/, "").trim();
          rowLabelFromColon = lcel.v.trim().endsWith(":");
          break;
        }
        // Stop at a non-string value (another field's data, not a label)
        if (lcel.v !== undefined && lcel.v !== null && lcel.v !== "") break;
      }

      // Find column label: scan above — only when no definitive colon-label was found on the left
      // (avoids noisy combinations like "SPEC ITEM File Version: 06/05/24")
      let colLabel = null;
      if (!rowLabelFromColon) {
        for (let ar = r - 1; ar >= blk.r0; ar--) {
          const acel = get(ar, c);
          if (acel && typeof acel.v === "string" && acel.v.trim().length > 1) {
            colLabel = acel.v.trim().replace(/:$/, "").trim();
            break;
          }
        }
      }

      // Determine section name and field label.
      // If rowLabel is a short, no-space, no-colon identifier (like TX100, TX400),
      // treat it as the section name and use colLabel as the field label.
      let label;
      let secName = "General Info";

      if (rowLabel && !rowLabelFromColon && !rowLabel.includes(" ") && rowLabel.length <= 10) {
        // Short code-like rowLabel → becomes the section name, not part of the field label
        secName = rowLabel;
        label = colLabel || XLSX.utils.encode_cell({ r, c });
      } else {
        // Normal: combine rowLabel + colLabel for field label
        if (rowLabel && colLabel && rowLabel !== colLabel) {
          label = `${rowLabel} ${colLabel}`;
        } else {
          label = rowLabel || colLabel || XLSX.utils.encode_cell({ r, c });
        }

        // Find the section title for this cell's own block, nearest above.
        // An ALL-CAPS header in the block's first few columns, or a lone string on
        // its own row within the block, which is how a person titles a block that
        // sits beside another one.
        let titled = false;
        outer: for (let sr = r - 1; sr >= blk.r0; sr--) {
          // Across the whole block, not just its first few columns. A block that
          // starts at column J carries its title at column J.
          for (let sc = blk.c0; sc <= blk.c1; sc++) {
            // A value sitting in an input cell is data, not a heading. A default
            // like "ASTM D6938" typed into a required field is all-caps and would
            // otherwise be read as a section title.
            if (isInputCell(sr, sc)) continue;
            const sc_cell = get(sr, sc);
            if (sc_cell && typeof sc_cell.v === "string" && isSectionHeader(sc_cell.v)) {
              secName = sc_cell.v.trim();
              titled = true;
              break outer;
            }
          }
          const lone = loneTextInBlock(G, sr, blk, isInputCell);
          if (lone) { secName = lone; titled = true; break; }
        }

        // Nothing titles this block from inside it. Fall back to the nearest title
        // above the block that sits in the same columns, so a block carries the
        // heading a reader would put it under. A title that does not overlap this
        // block horizontally belongs to something else and is left alone.
        if (!titled) {
          for (let sr = blk.r0 - 1; sr >= 0; sr--) {
            if (!isTitleRow(G, sr)) continue;
            const idx = (G[sr] || []).findIndex(fc => fc && fc.v !== undefined && fc.v !== null && fc.v !== "");
            if (idx >= blk.c0 && idx <= blk.c1) {
              secName = String(G[sr][idx].v).trim().replace(/:$/, "").trim();
              break;
            }
          }
        }
      }

      // For export: write to the merge's top-left cell (where Excel stores the value)
      const writeAddr = mergeTL || addr;

      const isRequired = requiredAddrs ? requiredAddrs.has(addr) || (mergeTL && requiredAddrs.has(mergeTL)) : false;
      highlightInputs.push({ r, c, label, secName, cell, writeAddr, isRequired });
    }
  }

  if (highlightInputs.length >= 1) {
    // Group by section; track label counts for Row N suffixes
    const sectionMap = {};
    highlightInputs.forEach(({ secName }) => {
      if (!sectionMap[secName]) sectionMap[secName] = { id: uid(), name: secName, fields: [], labelCount: {} };
    });
    highlightInputs.forEach(({ label, secName }) => {
      sectionMap[secName].labelCount[label] = (sectionMap[secName].labelCount[label] || 0) + 1;
    });

    const labelIdx = {};
    highlightInputs.forEach(({ c, label, secName, cell, writeAddr, isRequired }) => {
      const sec = sectionMap[secName];
      const count = sec.labelCount[label];
      const key = `${secName}__${label}`;
      labelIdx[key] = (labelIdx[key] || 0) + 1;
      const finalLabel = count > 1 ? `${label} — Row ${labelIdx[key]}` : label;
      const val = cellVal(cell);
      const fieldObj = {
        id: uid(),
        label: finalLabel,
        type: typeof val === "number" ? "number" : "text",
        options: "",
        defaultValue: val !== null && val !== undefined ? String(val) : "",
        _tableRow: count > 1 ? labelIdx[key] - 1 : undefined,
        _tableCol: count > 1 ? c : undefined,
        _tableHeaderRow: undefined,
        _addr: writeAddr,
      };
      if (isRequired) fieldObj.required = true;
      sec.fields.push(fieldObj);
    });

    const final = Object.values(sectionMap)
      .map(({ id, name, fields }) => ({
        id, name,
        description: `Imported from Excel — ${sheetName}`,
        isExcelImport: true,
        fields,
      }))
      .filter(s => s.fields.length > 0);

    if (final.length > 0) {
      return {
        id: uid(), name: sheetName,
        description: `Imported from Excel — ${sheetName}`,
        isExcelImport: true,
        sections: final,
      };
    }
  }

  // ─── Structural detection (no highlights found) ───────────────────────────
  const claimed = new Set();
  const sections = [];
  let curSec = { id: uid(), name: "General Info", fields: [] };

  function pushField(sec, label, defaultVal, fieldType) {
    sec.fields.push({ id: uid(), label, type: fieldType || "text", options: "", defaultValue: defaultVal || "" });
  }

  // ── PASS 1: Colon-label scan ─────────────────────────────────────────────
  for (let r = 0; r <= maxRow; r++) {
    for (let c = 0; c <= Math.min(3, maxCol); c++) {
      const cell = get(r, c);
      if (!cell || typeof cell.v !== "string") continue;
      if (isSectionHeader(cell.v.trim())) {
        if (curSec.fields.length > 0) sections.push(curSec);
        curSec = { id: uid(), name: cell.v.trim(), fields: [] };
        break;
      }
    }

    for (let c = 0; c <= maxCol; c++) {
      const cell = get(r, c);
      if (!cell || typeof cell.v !== "string") continue;
      if (!isLabel(cell.v)) continue;

      const label = cell.v.trim().replace(/:$/, "").trim();
      if (label.length < 2) continue;

      for (let vc = c + 1; vc <= Math.min(c + 8, maxCol); vc++) {
        const vc_cell = get(r, vc);
        const key = `${r},${vc}`;
        if (vc_cell && typeof vc_cell.v === "string" && isLabel(vc_cell.v)) break;
        if (vc_cell && typeof vc_cell.v === "string" && isSectionHeader(vc_cell.v)) break;
        if (claimed.has(key)) { if (vc_cell) break; continue; }
        if (isFormulaVal(vc_cell)) continue;

        const val = cellVal(vc_cell);
        const hasValue = val !== null && val !== undefined && val !== "";
        const isBlankTarget = !vc_cell || val === null || val === "";

        if (hasValue || isBlankTarget) {
          claimed.add(key);
          const isNum = typeof val === "number";
          pushField(curSec, label, hasValue ? String(val) : "", isNum ? "number" : "text");
          break;
        }
      }
    }
  }
  if (curSec.fields.length > 0) sections.push(curSec);

  // ── PASS 2: Input/Value table pattern ────────────────────────────────────
  let valueCol = -1;
  for (let r = 0; r <= Math.min(5, maxRow); r++) {
    for (let c = 0; c <= maxCol; c++) {
      const cell = get(r, c);
      if (cell && cell.v === "Value") { valueCol = c; break; }
    }
    if (valueCol >= 0) break;
  }

  if (valueCol >= 0) {
    const labelCol = valueCol - 1;
    let ovSecName = "Oversize Correction";
    for (let r = 0; r <= Math.min(5, maxRow); r++) {
      const hcell = get(r, labelCol);
      if (hcell && typeof hcell.v === "string" && isSectionHeader(hcell.v)) {
        ovSecName = hcell.v.trim();
        break;
      }
    }

    let ovSec = sections.find(s => s.name === ovSecName);
    if (!ovSec) { ovSec = { id: uid(), name: ovSecName, fields: [] }; sections.push(ovSec); }

    for (let r = 0; r <= maxRow; r++) {
      const lc = get(r, labelCol);
      const vc = get(r, valueCol);
      if (!lc || typeof lc.v !== "string") continue;
      const labelText = lc.v.trim();
      if (!labelText || isSectionHeader(labelText) || labelText === "Input" || labelText === "Notes") continue;
      if (isFormulaVal(vc)) continue;
      const key = `${r},${valueCol}`;
      if (claimed.has(key)) continue;
      claimed.add(key);
      const val = cellVal(vc);
      const isNum = typeof val === "number";
      pushField(ovSec, labelText, val !== null ? String(val) : "", isNum ? "number" : "text");
    }
  }

  // ── PASS 3: Data tables ──────────────────────────────────────────────────
  const headerInfoRows = new Set();
  for (let r = 0; r <= maxRow; r++) {
    for (let c = 0; c <= maxCol; c++) {
      const cell = get(r, c);
      if (cell && typeof cell.v === "string" && isLabel(cell.v)) {
        headerInfoRows.add(r);
        break;
      }
    }
  }

  const SKIP_LABELS = new Set(["input", "value", "notes", "section", "description"]);

  for (let r = 0; r <= maxRow - 1; r++) {
    if (headerInfoRows.has(r)) continue;

    const textCols = [];
    for (let c = 0; c <= maxCol; c++) {
      const cell = get(r, c);
      if (!cell || typeof cell.v !== "string") continue;
      const v = cell.v.trim();
      if (v.length < 2 || isLabel(v) || isSectionHeader(v)) continue;
      if (SKIP_LABELS.has(v.toLowerCase())) continue;
      const key = `${r},${c}`;
      if (claimed.has(key)) continue;
      textCols.push({ c, label: v });
    }
    if (textCols.length < 3) continue;

    const minCol = Math.min(...textCols.map(t => t.c));
    const maxColIdx = Math.max(...textCols.map(t => t.c));
    if (maxColIdx - minCol > 14) continue;

    let dataRows = 0;
    let blankStreak = 0;
    for (let dr = r + 1; dr <= Math.min(maxRow, r + 30); dr++) {
      const row = G[dr] || [];
      const hasHeader = row.some(cell => cell && typeof cell.v === "string" && (isSectionHeader(cell.v) || isLabel(cell.v)));
      if (hasHeader) break;
      if (isTitleRow(G, dr)) break;

      const headerColsInRow = textCols.map(({ c }) => row[c]).filter(Boolean);
      if (headerColsInRow.length > 0 && headerColsInRow.every(c => c.f)) break;

      const hasContent = row.some(cell => cell && cell.v !== undefined && cell.v !== null && cell.v !== "" && !cell.f);
      if (hasContent && headerColsInRow.filter(c => !c.f).length === 0) break;

      if (!hasContent) {
        blankStreak++;
        if (blankStreak > 8) break;
        dataRows++;
      } else {
        blankStreak = 0;
        dataRows++;
      }
    }
    if (dataRows < 1) continue;

    let secName = "Data Table";
    for (let sr = r - 1; sr >= Math.max(0, r - 5); sr--) {
      for (let sc = 0; sc <= 3; sc++) {
        const c = get(sr, sc);
        if (c && typeof c.v === "string" && isSectionHeader(c.v)) { secName = c.v.trim(); break; }
      }
      if (isTitleRow(G, sr)) {
        const filled = (G[sr] || []).find(c => c && c.v);
        if (filled) { secName = String(filled.v).trim(); break; }
      }
    }

    let tableSec = sections.find(s => s.name === secName);
    if (!tableSec) { tableSec = { id: uid(), name: secName, fields: [] }; sections.push(tableSec); }

    textCols.forEach(({ c, label }) => {
      let fCount = 0;
      for (let dr = 1; dr <= dataRows; dr++) { if (get(r + dr, c)?.f) fCount++; }
      if (fCount >= dataRows) return;

      for (let dr = 1; dr <= dataRows; dr++) {
        const dc = get(r + dr, c);
        const key = `${r + dr},${c}`;
        if (claimed.has(key) || dc?.f) continue;
        claimed.add(key);
        const val = dc ? cellVal(dc) : null;
        tableSec.fields.push({
          id: uid(), label: `${label} — Row ${dr}`,
          type: "number",
          options: "", defaultValue: val !== null ? String(val) : "",
          _tableRow: dr - 1, _tableCol: c, _tableHeaderRow: r,
        });
      }
    });
  }

  // ── Dedupe & filter ──────────────────────────────────────────────────────
  const final = sections
    .map(sec => {
      const seen = new Set();
      return { ...sec, fields: sec.fields.filter(f => { if (seen.has(f.label)) return false; seen.add(f.label); return true; }) };
    })
    .filter(s => s.fields.length > 0);

  return final.length > 0
    ? { id: uid(), name: sheetName, description: `Imported from Excel — ${sheetName}`, isExcelImport: true, sections: final }
    : null;
}

// ─── Public: import file ──────────────────────────────────────────────────────
export function importExcelAsTemplate(file, existingBuffer) {
  return new Promise((resolve, reject) => {
    const process = async (arrayBuffer) => {
      try {
        // Get highlighted cell map via JSZip (reliable color detection)
        let coloredCellMap = new Map();
        try {
          coloredCellMap = await getColoredCellMap(arrayBuffer);
        } catch (e) {
          console.warn("JSZip color detection failed, falling back to structural:", e);
        }

        const wb = XLSX.read(new Uint8Array(arrayBuffer), { type: "array", cellFormula: true, cellStyles: true });

        if (!wb.SheetNames?.length) { reject(new Error("No sheets found in workbook")); return; }

        // Skip hidden/very-hidden sheets
        const visibleNames = wb.SheetNames.filter((_, i) => {
          const state = wb.Workbook?.Sheets?.[i]?.Hidden;
          return !state; // 0 or undefined = visible
        });

        const templates = visibleNames.map(name => {
          try {
            const sheetColorData = coloredCellMap.get(name) || null;
            const addrs = sheetColorData?.inputs || null;
            const requiredAddrs = sheetColorData?.required || null;
            return parseSheetToTemplate(wb, name, addrs, requiredAddrs);
          } catch (err) {
            console.warn(`Sheet "${name}" parse error:`, err);
            return null;
          }
        }).filter(t => t && t.sections.reduce((n, s) => n + s.fields.length, 0) >= 1);

        if (!templates.length) {
          reject(new Error("No fillable fields detected in any sheet."));
          return;
        }
        resolve({ templates, workbook: wb, sheetNames: wb.SheetNames, visibleNames });
      } catch (err) {
        reject(err);
      }
    };

    if (existingBuffer) {
      process(existingBuffer).catch(reject);
    } else {
      const reader = new FileReader();
      reader.onload = (e) => process(e.target.result).catch(reject);
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsArrayBuffer(file);
    }
  });
}

// ─── JSZip-based helpers (shared with raw export) ────────────────────────────
const OOXML_NS = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
const OOXML_REL_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";

async function resolveSheetPathForName(zip, sheetName) {
  const wbXml = await zip.file("xl/workbook.xml").async("string");
  const wbDoc = new DOMParser().parseFromString(wbXml, "application/xml");
  const sheets = wbDoc.getElementsByTagNameNS(OOXML_NS, "sheet");
  let rId = null;
  for (const s of sheets) {
    if (s.getAttribute("name") === sheetName) {
      rId = s.getAttributeNS(OOXML_REL_NS, "id") || s.getAttribute("r:id");
      break;
    }
  }
  if (!rId) return null;
  const relsXml = await zip.file("xl/_rels/workbook.xml.rels").async("string");
  const relsDoc = new DOMParser().parseFromString(relsXml, "application/xml");
  for (const rel of relsDoc.getElementsByTagName("Relationship")) {
    if (rel.getAttribute("Id") === rId) {
      const target = rel.getAttribute("Target");
      return target.startsWith("/") ? target.slice(1) : `xl/${target}`;
    }
  }
  return null;
}

function colLetterToNum(col) {
  let n = 0;
  for (const ch of col) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}

function patchCellsInSheetXml(sheetXml, cellValues) {
  const doc = new DOMParser().parseFromString(sheetXml, "application/xml");
  if (doc.querySelector("parsererror")) return sheetXml;

  const cellMap = {};
  for (const c of doc.getElementsByTagNameNS(OOXML_NS, "c")) {
    const r = c.getAttribute("r");
    if (r) cellMap[r] = c;
  }
  const sheetData = doc.getElementsByTagNameNS(OOXML_NS, "sheetData")[0];

  for (const [addr, rawVal] of Object.entries(cellValues)) {
    if (rawVal === "" || rawVal == null) continue;
    const num = parseFloat(rawVal);
    const isNum = !isNaN(num);

    let cell = cellMap[addr];
    if (!cell) {
      const m = addr.match(/^([A-Z]+)(\d+)$/);
      if (!m) continue;
      const rowN = parseInt(m[2]);
      const allRows = Array.from(doc.getElementsByTagNameNS(OOXML_NS, "row"));
      let row = allRows.find(r => parseInt(r.getAttribute("r")) === rowN);
      if (!row) {
        row = doc.createElementNS(OOXML_NS, "row");
        row.setAttribute("r", String(rowN));
        const before = allRows.find(r => parseInt(r.getAttribute("r")) > rowN);
        if (before) sheetData.insertBefore(row, before); else sheetData.appendChild(row);
      }
      cell = doc.createElementNS(OOXML_NS, "c");
      cell.setAttribute("r", addr);
      const colN = colLetterToNum(m[1]);
      const rowCells = Array.from(row.getElementsByTagNameNS(OOXML_NS, "c"));
      const beforeCell = rowCells.find(c => {
        const cm = (c.getAttribute("r") || "").match(/^([A-Z]+)/);
        return cm && colLetterToNum(cm[1]) > colN;
      });
      if (beforeCell) row.insertBefore(cell, beforeCell); else row.appendChild(cell);
      cellMap[addr] = cell;
    }

    for (const tag of ["f", "v", "is"]) {
      for (const el of Array.from(cell.getElementsByTagNameNS(OOXML_NS, tag))) {
        cell.removeChild(el);
      }
    }

    if (isNum) {
      cell.removeAttribute("t");
      const v = doc.createElementNS(OOXML_NS, "v");
      v.textContent = String(num);
      cell.appendChild(v);
    } else {
      cell.setAttribute("t", "str");
      const v = doc.createElementNS(OOXML_NS, "v");
      v.textContent = String(rawVal);
      cell.appendChild(v);
    }
  }

  return new XMLSerializer().serializeToString(doc);
}

// ─── Internal: patch report values into raw bytes, return { bytes, isXlsm } ──
async function buildPatchedBytes(report, template, rawBytes) {
  const zip = await JSZip.loadAsync(rawBytes);

  if (template.isMultiSheet && (template.sheets || []).length > 0) {
    // Patch each sheet in the workbook with its own field values
    let patchedAny = false;
    for (const sheet of template.sheets) {
      const sheetCellValues = {};
      for (const sec of sheet.sections || []) {
        for (const field of sec.fields || []) {
          const val = report[field.id];
          if (val === undefined || val === null || val === "") continue;
          if (field._addr) sheetCellValues[field._addr] = val;
        }
      }
      const sheetPath = await resolveSheetPathForName(zip, sheet.name);
      if (!sheetPath || !zip.file(sheetPath)) continue;
      patchedAny = true;
      if (Object.keys(sheetCellValues).length > 0) {
        const patched = patchCellsInSheetXml(await zip.file(sheetPath).async("string"), sheetCellValues);
        zip.file(sheetPath, patched);
      }
    }
    if (!patchedAny) return null;
  } else {
    // Single-sheet: collect all field values and patch the template's sheet
    const cellValues = {};
    for (const sec of template.sections || []) {
      for (const field of sec.fields || []) {
        const val = report[field.id];
        if (val === undefined || val === null || val === "") continue;
        if (field._addr) cellValues[field._addr] = val;
      }
    }

    const sheetPath = await resolveSheetPathForName(zip, template.name);
    if (!sheetPath || !zip.file(sheetPath)) return null;

    if (Object.keys(cellValues).length > 0) {
      const patched = patchCellsInSheetXml(await zip.file(sheetPath).async("string"), cellValues);
      zip.file(sheetPath, patched);
    }
  }

  // Strip yellow (#FFFBCC) and red input/required fills so cells appear clean in output
  const stylesFile = zip.file("xl/styles.xml");
  if (stylesFile) {
    let stylesXml = await stylesFile.async("string");
    // Replace yellow input fills with no-fill
    stylesXml = stylesXml.replace(
      /<patternFill patternType="solid">\s*<fgColor (?:rgb|theme)[^/]*(?:FFFBCC|fffbcc)[^/]*\/>\s*(?:<bgColor[^/]*\/>\s*)?<\/patternFill>/gi,
      '<patternFill patternType="none"/>'
    );
    // Replace red required-field fills with no-fill (strip all solid red fills)
    stylesXml = stylesXml.replace(
      /<patternFill patternType="solid">\s*<fgColor rgb="([0-9A-Fa-f]{8})"[^/]*\/>\s*(?:<bgColor[^/]*\/>\s*)?<\/patternFill>/gi,
      (match, hex8) => isRedFill(hex8.slice(2)) ? '<patternFill patternType="none"/>' : match
    );
    zip.file("xl/styles.xml", stylesXml);
  }

  // Remove stale calculation chain; force recalc on open
  zip.remove("xl/calcChain.xml");
  const wbXml = await zip.file("xl/workbook.xml").async("string");
  const wbDoc = new DOMParser().parseFromString(wbXml, "application/xml");
  let calcPr = wbDoc.getElementsByTagNameNS(OOXML_NS, "calcPr")[0];
  if (!calcPr) {
    calcPr = wbDoc.createElementNS(OOXML_NS, "calcPr");
    wbDoc.getElementsByTagNameNS(OOXML_NS, "workbook")[0].appendChild(calcPr);
  }
  calcPr.setAttribute("calcMode", "auto");
  calcPr.setAttribute("fullCalcOnLoad", "1");
  zip.file("xl/workbook.xml", new XMLSerializer().serializeToString(wbDoc));

  // Strip chart caches so Excel recalculates chart data from patched cell values on open
  const chartPaths = Object.keys(zip.files).filter(p => /xl\/charts\/chart\d+\.xml$/i.test(p));
  for (const chartPath of chartPaths) {
    try {
      let chartXml = await zip.file(chartPath).async("string");
      // Remove numCache blocks (cached numeric series data)
      chartXml = chartXml.replace(/<c:numCache>[\s\S]*?<\/c:numCache>/g, "");
      // Remove strCache blocks (cached string/category data)
      chartXml = chartXml.replace(/<c:strCache>[\s\S]*?<\/c:strCache>/g, "");
      zip.file(chartPath, chartXml);
    } catch (e) {
      console.warn("[buildPatchedBytes] Could not strip chart cache for", chartPath, e);
    }
  }

  const contentTypes = await zip.file("[Content_Types].xml").async("string");
  const isXlsm = contentTypes.includes("macroEnabled");
  const bytes = await zip.generateAsync({ type: "uint8array", compression: "DEFLATE", compressionOptions: { level: 6 } });
  return { bytes, isXlsm };
}

// ─── Public: export using raw bytes (preserves all formatting & formulas) ─────
export async function exportReportAsExcelRaw(report, template, rawBytes) {
  if (!rawBytes) return false;
  try {
    const result = await buildPatchedBytes(report, template, rawBytes);
    if (!result) return false;
    const { bytes, isXlsm } = result;
    const ext = isXlsm ? "xlsm" : "xlsx";
    const mime = isXlsm
      ? "application/vnd.ms-excel.sheet.macroEnabled.12"
      : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    const filename = `${(report.reportTitle || "report").replace(/\s+/g, "_")}_filled.${ext}`;
    const blob = new Blob([bytes], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return true;
  } catch (err) {
    console.error("Raw Excel export failed:", err);
    return false;
  }
}

// ─── Formula evaluator ────────────────────────────────────────────────────────
// Evaluates formula cells in a SheetJS worksheet in-place.
// Handles common Excel functions: SUM, AVERAGE, ROUND, IFERROR, IF, COUNT, MAX, MIN.
// Runs multiple passes to resolve chained dependencies.
function evalCellFormulas(ws, wb) {
  const getVal = (addr) => {
    const cell = ws[addr.replace(/\$/g, "").toUpperCase()];
    return cell ? cell.v : undefined;
  };

  // Lookup a value from another sheet in the workbook (cross-sheet refs like Formulas!B5)
  const getCrossSheetVal = (sheetName, addr) => {
    if (!wb) return undefined;
    const refWs = wb.Sheets[sheetName];
    if (!refWs) return undefined;
    const cell = refWs[addr.replace(/\$/g, "").toUpperCase()];
    return cell ? cell.v : undefined;
  };

  const expandRange = (rangeStr) => {
    const parts = rangeStr.trim().replace(/\$/g, "").split(":");
    if (parts.length === 1) return [parts[0].toUpperCase()];
    try {
      const s = XLSX.utils.decode_cell(parts[0]);
      const e = XLSX.utils.decode_cell(parts[1]);
      const addrs = [];
      for (let r = s.r; r <= e.r; r++)
        for (let c = s.c; c <= e.c; c++)
          addrs.push(XLSX.utils.encode_cell({ r, c }));
      return addrs;
    } catch { return []; }
  };

  // ── Formula evaluator ────────────────────────────────────────────────────
  // evalExpr tokenizes a formula and walks a recursive-descent parse tree to
  // a typed value (number, string or boolean). It never builds a JavaScript
  // source string and hands it to Function()/eval() — issue #2: a crafted
  // workbook could otherwise run arbitrary JS in whoever's browser opens a
  // report built from it. Typed evaluation also fixes issue #4: a branch of
  // IF/IFERROR/AND/OR that resolves to a string is returned as that string,
  // rather than being coerced into bare, unquoted text that a later
  // Function() call throws a ReferenceError on (the actual, previously
  // silent, cause of every PASS/FAIL cell in the ASTM library going dark).
  //
  // Grammar, low to high precedence:
  //   comparison := additive (( = | <> | <= | >= | < | > ) additive)*
  //   additive   := term (( + | - ) term)*
  //   term       := unary (( * | / ) unary)*
  //   unary      := '-' unary | power
  //   power      := primary ( '^' unary )?
  //   primary    := NUMBER | STRING | TRUE | FALSE | funcCall
  //               | cellRef | range | '(' comparison ')'
  const tokenizeFormula = (src) => {
    const tokens = [];
    const isDigit = (c) => c >= "0" && c <= "9";
    const isIdentStart = (c) => /[A-Za-z_]/.test(c);
    const isIdentChar = (c) => /[A-Za-z0-9_]/.test(c);
    let i = 0;
    while (i < src.length) {
      const c = src[i];
      if (c === " " || c === "\t" || c === "\n" || c === "\r") { i++; continue; }
      if (c === '"') {
        let j = i + 1, s = "";
        while (j < src.length) {
          if (src[j] === '"' && src[j + 1] === '"') { s += '"'; j += 2; continue; }
          if (src[j] === '"') break;
          s += src[j]; j++;
        }
        tokens.push({ t: "STR", v: s }); i = j + 1; continue;
      }
      if (c === "'") {
        // Quoted sheet name — only meaningful immediately followed by '!'.
        let j = i + 1, s = "";
        while (j < src.length) {
          if (src[j] === "'" && src[j + 1] === "'") { s += "'"; j += 2; continue; }
          if (src[j] === "'") break;
          s += src[j]; j++;
        }
        j++;
        if (src[j] === "!") { tokens.push({ t: "SHEET", v: s }); i = j + 1; continue; }
        tokens.push({ t: "STR", v: s }); i = j; continue;
      }
      if (isDigit(c) || (c === "." && isDigit(src[i + 1]))) {
        let j = i, s = "";
        while (j < src.length && isDigit(src[j])) { s += src[j]; j++; }
        if (src[j] === ".") { s += "."; j++; while (j < src.length && isDigit(src[j])) { s += src[j]; j++; } }
        if (src[j] === "e" || src[j] === "E") {
          let k = j + 1, es = src[j];
          if (src[k] === "+" || src[k] === "-") { es += src[k]; k++; }
          if (isDigit(src[k])) { while (k < src.length && isDigit(src[k])) { es += src[k]; k++; } s += es; j = k; }
        }
        tokens.push({ t: "NUM", v: parseFloat(s) }); i = j; continue;
      }
      if (c === "$" || isIdentStart(c)) {
        let j = i, s = "";
        while (j < src.length && (src[j] === "$" || isIdentChar(src[j]))) { s += src[j]; j++; }
        if (src[j] === "!" && /^[A-Za-z_][A-Za-z0-9_]*$/.test(s)) {
          tokens.push({ t: "SHEET", v: s }); i = j + 1; continue;
        }
        tokens.push({ t: "IDENT", v: s }); i = j; continue;
      }
      if (c === "<" && src[i + 1] === ">") { tokens.push({ t: "CMP", v: "<>" }); i += 2; continue; }
      if (c === "<" && src[i + 1] === "=") { tokens.push({ t: "CMP", v: "<=" }); i += 2; continue; }
      if (c === ">" && src[i + 1] === "=") { tokens.push({ t: "CMP", v: ">=" }); i += 2; continue; }
      if (c === "=" || c === "<" || c === ">") { tokens.push({ t: "CMP", v: c }); i++; continue; }
      if ("+-*/^(),:".includes(c)) { tokens.push({ t: c }); i++; continue; }
      i++; // unrecognised character: skip rather than throw
    }
    return tokens;
  };

  const CELL_RE = /^\$?([A-Za-z]{1,3})\$?([0-9]+)$/;

  const parseFormula = (tokens) => {
    let p = 0;
    const peek = () => tokens[p];
    const next = () => tokens[p++];
    const expect = (t) => { const tok = next(); if (!tok || tok.t !== t) throw new Error("parse error"); return tok; };

    const parseCellOrRange = (sheet) => {
      const identTok = expect("IDENT");
      if (!CELL_RE.test(identTok.v)) return { type: "ref", sheet, addr: null };
      const from = identTok.v.replace(/\$/g, "").toUpperCase();
      if (peek() && peek().t === ":") {
        next();
        const toTok = expect("IDENT");
        return { type: "range", sheet, from, to: toTok.v.replace(/\$/g, "").toUpperCase() };
      }
      return { type: "ref", sheet, addr: from };
    };

    const parsePrimary = () => {
      const tok = peek();
      if (!tok) throw new Error("unexpected end of formula");
      if (tok.t === "NUM") { next(); return { type: "num", value: tok.v }; }
      if (tok.t === "STR") { next(); return { type: "str", value: tok.v }; }
      if (tok.t === "SHEET") { next(); return parseCellOrRange(tok.v); }
      if (tok.t === "(") {
        next();
        const inner = parseComparison();
        expect(")");
        return inner;
      }
      if (tok.t === "IDENT") {
        next();
        const upper = tok.v.toUpperCase();
        if (upper === "TRUE") return { type: "bool", value: true };
        if (upper === "FALSE") return { type: "bool", value: false };
        if (peek() && peek().t === "(") {
          next();
          const args = [];
          if (peek() && peek().t !== ")") {
            args.push(parseComparison());
            while (peek() && peek().t === ",") { next(); args.push(parseComparison()); }
          }
          expect(")");
          return { type: "call", name: upper, args };
        }
        if (CELL_RE.test(tok.v)) {
          const from = tok.v.replace(/\$/g, "").toUpperCase();
          if (peek() && peek().t === ":") {
            next();
            const toTok = expect("IDENT");
            return { type: "range", sheet: null, from, to: toTok.v.replace(/\$/g, "").toUpperCase() };
          }
          return { type: "ref", sheet: null, addr: from };
        }
        // Bare word: not a keyword, not a function call, not a cell address.
        return { type: "ref", sheet: null, addr: null };
      }
      throw new Error("unexpected token " + tok.t);
    };

    const parsePower = () => {
      const base = parsePrimary();
      if (peek() && peek().t === "^") { next(); return { type: "bin", op: "^", left: base, right: parseUnary() }; }
      return base;
    };

    const parseUnary = () => {
      if (peek() && peek().t === "-") { next(); return { type: "unary", op: "-", node: parseUnary() }; }
      return parsePower();
    };

    const parseTerm = () => {
      let node = parseUnary();
      while (peek() && (peek().t === "*" || peek().t === "/")) {
        const op = next().t;
        node = { type: "bin", op, left: node, right: parseUnary() };
      }
      return node;
    };

    const parseAdditive = () => {
      let node = parseTerm();
      while (peek() && (peek().t === "+" || peek().t === "-")) {
        const op = next().t;
        node = { type: "bin", op, left: node, right: parseTerm() };
      }
      return node;
    };

    function parseComparison() {
      let node = parseAdditive();
      while (peek() && peek().t === "CMP") {
        const op = next().v;
        node = { type: "cmp", op, left: node, right: parseAdditive() };
      }
      return node;
    }

    const tree = parseComparison();
    if (p !== tokens.length) throw new Error("trailing tokens");
    return tree;
  };

  const truthy = (v) => {
    if (typeof v === "boolean") return v;
    if (v === undefined || v === null || v === "") return false;
    if (typeof v === "number") return v !== 0 && !Number.isNaN(v);
    return true;
  };

  const numOf = (v) => {
    if (v === undefined || v === null || v === "") return 0;
    if (typeof v === "boolean") return v ? 1 : 0;
    return Number(v);
  };

  const resolveRef = (sheet, addr) => {
    if (!addr) return undefined;
    return sheet ? getCrossSheetVal(sheet, addr) : getVal(addr);
  };

  const resolveRangeVals = (sheet, from, to, { numericOnly = true } = {}) => {
    const addrs = expandRange(`${from}:${to}`);
    const out = [];
    for (const a of addrs) {
      const v = sheet ? getCrossSheetVal(sheet, a) : getVal(a);
      if (numericOnly) { if (typeof v === "number") out.push(v); }
      else out.push(v);
    }
    return out;
  };

  // Flatten a mix of range / single-ref / expression argument nodes into a
  // flat number array, ignoring blank and non-numeric cells — matches
  // Excel's SUM/COUNT/MAX/MIN/AVERAGE behaviour over ranges.
  const flattenNumericArgs = (nodes) => {
    const out = [];
    for (const n of nodes) {
      if (n.type === "range") { out.push(...resolveRangeVals(n.sheet, n.from, n.to)); continue; }
      if (n.type === "ref") { const v = resolveRef(n.sheet, n.addr); if (typeof v === "number") out.push(v); continue; }
      const v = evalAstNode(n);
      if (typeof v === "number" && !Number.isNaN(v)) out.push(v);
    }
    return out;
  };

  // COUNTIF criteria: a leading comparison operator (">=95"), or an exact
  // match (numeric or case-insensitive text) when there is none.
  const matchCriteria = (val, crit) => {
    if (typeof crit === "string") {
      const m = crit.match(/^(<=|>=|<>|=|<|>)(.*)$/);
      if (m) {
        const op = m[1], rhsRaw = m[2].trim();
        const rhsNum = parseFloat(rhsRaw);
        const lhsNum = typeof val === "number" ? val : parseFloat(val);
        if (rhsRaw !== "" && !Number.isNaN(rhsNum) && !Number.isNaN(lhsNum)) {
          switch (op) {
            case "=": return lhsNum === rhsNum;
            case "<>": return lhsNum !== rhsNum;
            case "<": return lhsNum < rhsNum;
            case ">": return lhsNum > rhsNum;
            case "<=": return lhsNum <= rhsNum;
            case ">=": return lhsNum >= rhsNum;
            default: return false;
          }
        }
        const ls = String(val ?? "").toUpperCase(), rs = rhsRaw.toUpperCase();
        if (op === "=") return ls === rs;
        if (op === "<>") return ls !== rs;
        return false;
      }
    }
    if (typeof crit === "number") return Number(val) === crit;
    return String(val ?? "").toUpperCase() === String(crit).toUpperCase();
  };

  // Excel compares numerically when both sides look numeric, and does a
  // case-insensitive text compare otherwise — blanks read as 0 against a
  // number and "" against text, same as a blank cell in arithmetic.
  const compareVals = (op, a, b) => {
    if (a === undefined || a === null) a = typeof b === "string" ? "" : 0;
    if (b === undefined || b === null) b = typeof a === "string" ? "" : 0;
    if (typeof a === "string" || typeof b === "string") {
      const as = String(a).toUpperCase(), bs = String(b).toUpperCase();
      switch (op) {
        case "=": return as === bs;
        case "<>": return as !== bs;
        case "<": return as < bs;
        case ">": return as > bs;
        case "<=": return as <= bs;
        case ">=": return as >= bs;
        default: return false;
      }
    }
    const an = numOf(a), bn = numOf(b);
    switch (op) {
      case "=": return an === bn;
      case "<>": return an !== bn;
      case "<": return an < bn;
      case ">": return an > bn;
      case "<=": return an <= bn;
      case ">=": return an >= bn;
      default: return false;
    }
  };

  const CALLABLE = {
    IF: (args) => {
      if (args.length < 2) return undefined;
      return truthy(evalAstNode(args[0]))
        ? (args[1] !== undefined ? evalAstNode(args[1]) : undefined)
        : (args[2] !== undefined ? evalAstNode(args[2]) : false);
    },
    IFERROR: (args) => {
      if (args.length < 2) return undefined;
      let v;
      try { v = evalAstNode(args[0]); } catch { v = undefined; }
      const bad = v === undefined || (typeof v === "number" && Number.isNaN(v));
      return bad ? evalAstNode(args[1]) : v;
    },
    AND: (args) => args.every((a) => truthy(evalAstNode(a))),
    OR: (args) => args.some((a) => truthy(evalAstNode(a))),
    ABS: (args) => Math.abs(numOf(evalAstNode(args[0]))),
    SQRT: (args) => Math.sqrt(numOf(evalAstNode(args[0]))),
    // Logs. The referee method for liquid limit in ASTM D4318 is a flow curve of
    // moisture against the log of the blow count, read at 25 blows, so a soils
    // template cannot express its own standard without these.
    LOG10: (args) => Math.log10(numOf(evalAstNode(args[0]))),
    LN: (args) => Math.log(numOf(evalAstNode(args[0]))),
    LOG: (args) => {
      const v = numOf(evalAstNode(args[0]));
      const base = args.length > 1 ? numOf(evalAstNode(args[1])) : 10;
      return Math.log(v) / Math.log(base);
    },
    // Counts values of any type, including text. COUNT only counts numbers, so
    // counting a column of specimen IDs silently returned zero.
    COUNTA: (args) => {
      let n = 0;
      for (const node of args) {
        if (node.type === "range") {
          for (const v of resolveRangeVals(node.sheet, node.from, node.to, { numericOnly: false })) {
            if (v !== undefined && v !== null && v !== "") n++;
          }
          continue;
        }
        const v = node.type === "ref" ? resolveRef(node.sheet, node.addr) : evalAstNode(node);
        if (v !== undefined && v !== null && v !== "") n++;
      }
      return n;
    },
    // Same rounding as the previous evaluator (toFixed), so already-verified
    // template output doesn't shift by a float-formatting quirk.
    ROUND: (args) => {
      const v = numOf(evalAstNode(args[0]));
      if (Number.isNaN(v)) return undefined;
      const d = Math.max(0, Math.min(15, Math.trunc(numOf(evalAstNode(args[1])))));
      return Number(v.toFixed(d));
    },
    ROUNDUP: (args) => {
      const v = numOf(evalAstNode(args[0]));
      if (Number.isNaN(v)) return undefined;
      const d = Math.max(0, Math.trunc(numOf(evalAstNode(args[1]))));
      const f = Math.pow(10, d);
      return (Math.sign(v) || 1) * Math.ceil(Math.abs(v) * f) / f;
    },
    ROUNDDOWN: (args) => {
      const v = numOf(evalAstNode(args[0]));
      if (Number.isNaN(v)) return undefined;
      const d = Math.max(0, Math.trunc(numOf(evalAstNode(args[1]))));
      const f = Math.pow(10, d);
      return (Math.sign(v) || 1) * Math.floor(Math.abs(v) * f) / f;
    },
    ISBLANK: (args) => {
      const n = args[0];
      const v = n && n.type === "ref" ? resolveRef(n.sheet, n.addr) : evalAstNode(n);
      return v === undefined || v === null || v === "";
    },
    SUM: (args) => flattenNumericArgs(args).reduce((s, v) => s + v, 0),
    COUNT: (args) => flattenNumericArgs(args).length,
    MAX: (args) => { const n = flattenNumericArgs(args); return n.length ? Math.max(...n) : 0; },
    MIN: (args) => { const n = flattenNumericArgs(args); return n.length ? Math.min(...n) : 0; },
    AVERAGE: (args) => { const n = flattenNumericArgs(args); return n.length ? n.reduce((s, v) => s + v, 0) / n.length : 0; },
    COUNTIF: (args) => {
      if (args.length < 2) return 0;
      const rangeNode = args[0];
      const crit = evalAstNode(args[1]);
      let vals;
      if (rangeNode.type === "range") vals = resolveRangeVals(rangeNode.sheet, rangeNode.from, rangeNode.to, { numericOnly: false });
      else if (rangeNode.type === "ref") vals = [resolveRef(rangeNode.sheet, rangeNode.addr)];
      else vals = [evalAstNode(rangeNode)];
      return vals.filter((v) => v !== undefined && v !== null && v !== "" && matchCriteria(v, crit)).length;
    },
  };

  const evalAstNode = (node) => {
    switch (node.type) {
      case "num": return node.value;
      case "str": return node.value;
      case "bool": return node.value;
      case "ref": return resolveRef(node.sheet, node.addr);
      case "range": return undefined; // only meaningful inside a range-aware function
      case "unary": return -numOf(evalAstNode(node.node));
      case "bin": {
        const l = numOf(evalAstNode(node.left)), r = numOf(evalAstNode(node.right));
        switch (node.op) {
          case "+": return l + r;
          case "-": return l - r;
          case "*": return l * r;
          case "/": return r === 0 ? undefined : l / r;
          case "^": return Math.pow(l, r);
          default: return undefined;
        }
      }
      case "cmp": return compareVals(node.op, evalAstNode(node.left), evalAstNode(node.right));
      case "call": {
        const fn = CALLABLE[node.name];
        if (!fn) return undefined; // unknown "function": no path to execute anything
        try { return fn(node.args); } catch { return undefined; }
      }
      default: return undefined;
    }
  };

  const evalExpr = (formulaText) => {
    const src = (formulaText ?? "").toString().trim();
    if (!src) return undefined;
    try {
      const tree = parseFormula(tokenizeFormula(src));
      let result = evalAstNode(tree);
      if (typeof result === "boolean") result = result ? "TRUE" : "FALSE";
      if (typeof result === "number" && Number.isNaN(result)) return undefined;
      return result;
    } catch { return undefined; }
  };

  // ── Complex formula support: LET / LINEST / INDEX / CHOOSE ──────────────────

  // Like getVal but returns NaN for error cells
  const getNumVal = (addr) => {
    const cell = ws[addr.replace(/\$/g, "").toUpperCase()];
    if (!cell || cell.t === "e") return NaN;
    return typeof cell.v === "number" ? cell.v : parseFloat(cell.v);
  };

  // Split comma-delimited args respecting nested parens/braces
  const splitArgs = (str) => {
    const args = []; let depth = 0, start = 0;
    for (let i = 0; i < str.length; i++) {
      const c = str[i];
      if (c === "(" || c === "{") depth++;
      else if (c === ")" || c === "}") depth--;
      else if (c === "," && depth === 0) { args.push(str.slice(start, i).trim()); start = i + 1; }
    }
    args.push(str.slice(start).trim());
    return args;
  };

  // Expand a range expression to an array of numbers.
  // Handles: plain range, cross-sheet range, range^N, (range*scalar)^N
  const evalRangeToArray = (expr) => {
    // Strip $ signs, trim
    let e = expr.trim().replace(/\$/g, "");

    // Helper: strip outermost matching parens
    const stripParens = (s) => {
      if (!s.startsWith("(") || !s.endsWith(")")) return s;
      let d = 0;
      for (let i = 0; i < s.length - 1; i++) {
        if (s[i] === "(" || s[i] === "{") d++;
        else if (s[i] === ")" || s[i] === "}") d--;
        if (d === 0) return s; // outer paren closed early, not a wrapper
      }
      return s.slice(1, -1);
    };

    e = stripParens(e);

    // Detect trailing ^N (integer power, not ^{...})
    const powMatch = e.match(/^(.+?)\^(\d+)$/);
    let core = powMatch ? stripParens(powMatch[1].trim()) : e;
    const power = powMatch ? parseInt(powMatch[2]) : 1;
    const applyPow = (v) => power === 1 ? v : Math.pow(v, power);

    // Handle range*scalar or scalar*range (e.g. B2:B5*E1)
    const mulRng = core.match(/^([A-Z]+\d+:[A-Z]+\d+)\*([A-Z]+\d+)$/i);
    const mulScl = core.match(/^([A-Z]+\d+)\*([A-Z]+\d+:[A-Z]+\d+)$/i);
    if (mulRng || mulScl) {
      const m = mulRng || mulScl;
      const rangeStr = mulRng ? m[1] : m[2];
      const scalarAddr = mulRng ? m[2] : m[1];
      const scalar = getNumVal(scalarAddr);
      if (isNaN(scalar)) return [];
      return expandRange(rangeStr).map(a => applyPow(getNumVal(a) * scalar)).filter(v => !isNaN(v));
    }

    // Cross-sheet range
    if (core.includes(":")) {
      const crossMatch = core.match(/^'?([^'!]+)'?!(.+)$/i);
      if (crossMatch) {
        const refWs = wb?.Sheets[crossMatch[1]];
        if (!refWs) return [];
        return expandRange(crossMatch[2]).map(a => {
          const cell = refWs[a.replace(/\$/g, "").toUpperCase()];
          if (!cell || cell.t === "e") return NaN;
          return applyPow(typeof cell.v === "number" ? cell.v : parseFloat(cell.v));
        }).filter(v => !isNaN(v));
      }
      return expandRange(core).map(a => applyPow(getNumVal(a))).filter(v => !isNaN(v));
    }

    // Single cell
    const v = getNumVal(core);
    return isNaN(v) ? [] : [applyPow(v)];
  };

  // Quadratic LINEST: y = a*x^2 + b*x + c → returns [a, b, c]
  const linestQuadratic = (ys, xs) => {
    const n = ys.length;
    if (n < 3 || xs.length < n) return null;
    let s0=0,s1=0,s2=0,s3=0,s4=0,sy=0,sxy=0,sx2y=0;
    for (let i = 0; i < n; i++) {
      const x=xs[i], y=ys[i], x2=x*x;
      s0+=1; s1+=x; s2+=x2; s3+=x2*x; s4+=x2*x2;
      sy+=y; sxy+=x*y; sx2y+=x2*y;
    }
    const A=[[s4,s3,s2],[s3,s2,s1],[s2,s1,s0]], B=[sx2y,sxy,sy];
    for (let col=0; col<3; col++) {
      let mx=col;
      for (let r=col+1; r<3; r++) if (Math.abs(A[r][col])>Math.abs(A[mx][col])) mx=r;
      [A[col],A[mx]]=[A[mx],A[col]]; [B[col],B[mx]]=[B[mx],B[col]];
      if (Math.abs(A[col][col])<1e-12) return null;
      for (let r=col+1; r<3; r++) {
        const f=A[r][col]/A[col][col];
        for (let k=col; k<3; k++) A[r][k]-=f*A[col][k];
        B[r]-=f*B[col];
      }
    }
    const c=[0,0,0];
    for (let i=2; i>=0; i--) {
      c[i]=B[i];
      for (let j=i+1; j<3; j++) c[i]-=A[i][j]*c[j];
      c[i]/=A[i][i];
    }
    return c;
  };

  // Linear LINEST: y = m*x + b → returns [m, b]
  const linestLinear = (ys, xs) => {
    const n = ys.length;
    if (n < 2 || xs.length < n) return null;
    let sx=0,sy=0,sxy=0,sx2=0;
    for (let i=0; i<n; i++) { sx+=xs[i]; sy+=ys[i]; sxy+=xs[i]*ys[i]; sx2+=xs[i]*xs[i]; }
    const denom=n*sx2-sx*sx;
    if (Math.abs(denom)<1e-12) return null;
    const m=(n*sxy-sx*sy)/denom;
    return [m, (sy-m*sx)/n];
  };

  // Evaluate LINEST(...) args, returning coefficient array matching Excel's column order.
  //
  // Excel LINEST returns coefficients RIGHT-TO-LEFT per predictor column, then intercept:
  //   LINEST(y, CHOOSE({1,2}, x^2, x))  → [b_linear, a_quad, c]  (x is rightmost → col 1)
  //   LINEST(y, x^{1,2})                → [a_quad,  b_linear, c]  (x^2 is rightmost → col 1)
  const computeLinest = (argsStr) => {
    const args = splitArgs(argsStr);
    if (args.length < 2) return null;
    const ys = evalRangeToArray(args[0]);
    if (ys.length < 2) return null;
    const xsExpr = args[1].trim();

    // CHOOSE({1,2}, x^2, x) — predictors left-to-right: x^2, x
    // Excel col order: [coef_for_x (rightmost), coef_for_x^2, intercept] = [b, a, c]
    const chooseMatch = xsExpr.match(/^CHOOSE\s*\(\s*\{1,2\}\s*,(.+)\)$/i);
    if (chooseMatch) {
      const ca = splitArgs(chooseMatch[1]);
      if (ca.length >= 2) {
        const xs = evalRangeToArray(ca[1].trim()); // second CHOOSE arg = plain x range
        if (xs.length >= 3 && xs.length === ys.length) {
          const r = linestQuadratic(ys, xs); // [a, b, c]
          return r ? [r[1], r[0], r[2]] : null; // swap to [b, a, c] to match Excel
        }
      }
    }

    // range^{1,2} — predictors: x^1, x^2; Excel col order: [coef_for_x^2, coef_for_x, c] = [a, b, c]
    const powArrayMatch = xsExpr.replace(/\$/g, "").match(/^(.+?)\^\s*\{1,2\}$/);
    if (powArrayMatch) {
      const xs = evalRangeToArray(powArrayMatch[1].trim());
      if (xs.length >= 3 && xs.length === ys.length) return linestQuadratic(ys, xs); // [a, b, c]
    }

    // Linear
    const xs = evalRangeToArray(xsExpr);
    if (xs.length < 2 || xs.length !== ys.length) return null;
    return linestLinear(ys, xs);
  };

  // Evaluate LET(name1, val1, ..., body)
  const evalLet = (argsStr) => {
    const args = splitArgs(argsStr);
    if (args.length < 3 || args.length % 2 === 0) return undefined;
    const scalars = {}, arrays = {};
    const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const subst = (expr) => {
      // Sort by length desc to avoid partial-name replacement
      const entries = Object.entries(scalars).sort((a,b) => b[0].length - a[0].length);
      let e = expr;
      for (const [k,v] of entries) {
        if (typeof v === "number") e = e.replace(new RegExp(esc(k), "g"), v.toString());
      }
      return e;
    };
    for (let i = 0; i + 1 < args.length - 1; i += 2) {
      const name = args[i].trim();
      const valExpr = args[i+1].trim();
      if (/^LINEST\s*\(/i.test(valExpr)) {
        const inner = valExpr.slice(valExpr.indexOf("(")+1, valExpr.lastIndexOf(")"));
        const res = computeLinest(inner);
        if (res) arrays[name] = res;
      } else if (/^INDEX\s*\(/i.test(valExpr)) {
        const inner = valExpr.slice(valExpr.indexOf("(")+1, valExpr.lastIndexOf(")"));
        const idxArgs = splitArgs(inner);
        const refName = idxArgs[0]?.trim();
        const arr = arrays[refName];
        if (Array.isArray(arr)) {
          const col = idxArgs.length >= 3 ? parseInt(idxArgs[2])-1 : parseInt(idxArgs[1])-1;
          scalars[name] = isNaN(col) ? NaN : (arr[col] ?? NaN);
        }
      } else {
        const v = evalExpr(subst(valExpr));
        if (v !== undefined && !isNaN(v)) scalars[name] = v;
      }
    }
    return evalExpr(subst(args[args.length-1]));
  };

  // Try to evaluate formulas that evalExpr can't handle (LET, standalone INDEX+LINEST)
  const evalComplex = (formula) => {
    if (!formula) return undefined;
    const f = formula.trim();
    if (/^LET\s*\(/i.test(f)) {
      try {
        const inner = f.slice(f.indexOf("(")+1, f.lastIndexOf(")"));
        return evalLet(inner);
      } catch { return undefined; }
    }
    const ilm = f.match(/^INDEX\s*\(\s*LINEST\s*\((.+)\)\s*,\s*1\s*,\s*(\d+)\s*\)$/i);
    if (ilm) {
      const coefs = computeLinest(ilm[1]);
      if (coefs) return coefs[parseInt(ilm[2])-1];
    }
    return undefined;
  };

  // Multiple passes to handle chains (formula A depends on formula B)
  for (let pass = 0; pass < 6; pass++) {
    let changed = false;
    for (const [addr, cell] of Object.entries(ws)) {
      if (addr.startsWith("!") || !cell?.f) continue;
      const newVal = evalComplex(cell.f) ?? evalExpr(cell.f);
      if (newVal !== undefined && newVal !== cell.v) {
        cell.v = newVal;
        // Regenerate cell.w using the cell's Excel number format, so the renderer
        // shows formatted values (e.g. "57.01") instead of raw floats ("57.01234…")
        if (cell.z && typeof newVal === "number") {
          try { cell.w = XLSX.SSF.format(cell.z, newVal); } catch(e) { delete cell.w; }
        } else {
          delete cell.w;
        }
        changed = true;
      }
    }
    if (!changed) break;
  }
}

// ─── Public: generate PDF from the full rendered Excel sheet ──────────────────
// Patches values into raw bytes, renders the sheet as an HTML table, then
// uses html2canvas → jsPDF to produce a PDF that looks like the original template.
export async function exportReportAsExcelPDF(report, template, rawBytes, jsPDFClass, chartImagesBySheet = {}, returnBlob = false) {
  if (!rawBytes || !jsPDFClass) return false;

  // Multi-sheet: render each sheet and combine into one PDF
  if (template?.isMultiSheet && (template.sheets || []).length > 0) {
    try {
      const html2canvas = (await import("html2canvas")).default;
      const doc = new jsPDFClass({ unit: "pt", format: "letter", orientation: "portrait" });
      let firstPage = true;

      const addImagePages = async (canvas, doc, firstPage) => {
        const imgW = canvas.width / 2;
        const imgH = canvas.height / 2;
        if (!firstPage) doc.addPage();
        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();
        const margin = 18;
        const availW = pageW - margin * 2;
        const availH = pageH - margin * 2;
        const scaleX = availW / imgW;
        const totalDrawH = imgH * scaleX;
        const pagesNeeded = Math.ceil(totalDrawH / availH);
        for (let p = 0; p < pagesNeeded; p++) {
          if (p > 0) doc.addPage();
          const srcYStart = (p * availH / scaleX) * 2;
          const srcSliceH = Math.min((availH / scaleX) * 2, canvas.height - srcYStart);
          const destDrawH = (srcSliceH / 2) * scaleX;
          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = canvas.width; sliceCanvas.height = srcSliceH;
          sliceCanvas.getContext("2d").drawImage(canvas, 0, srcYStart, canvas.width, srcSliceH, 0, 0, canvas.width, srcSliceH);
          doc.addImage(sliceCanvas.toDataURL("image/jpeg", 0.92), "JPEG", margin, margin, availW, destDrawH);
        }
        return pagesNeeded;
      };

      for (const sheet of template.sheets) {
        const sheetTpl = { ...sheet, isExcelImport: true, id: template.id };
        const htmlStr = await buildExcelPreviewHtml(report, sheetTpl, rawBytes);
        if (!htmlStr) continue;

        const container = document.createElement("div");
        container.style.cssText = "position:fixed;top:-9999px;left:-9999px;background:white;padding:8px;width:max-content;";
        container.innerHTML = htmlStr;
        document.body.appendChild(container);

        try {
          const canvas = await html2canvas(container, { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false, width: container.scrollWidth, height: container.scrollHeight });
          document.body.removeChild(container);
          await addImagePages(canvas, doc, firstPage);
          firstPage = false;

          // Append chart images for this sheet
          const chartImgs = (chartImagesBySheet[sheet.name] || []).filter(Boolean);
          for (const dataUrl of chartImgs) {
            doc.addPage();
            const pageW = doc.internal.pageSize.getWidth();
            const margin = 18;
            const availW = pageW - margin * 2;
            // Draw chart at fixed height preserving aspect ratio
            const chartH = Math.round(availW * (350 / 800));
            doc.addImage(dataUrl, "PNG", margin, margin, availW, chartH);
          }
        } catch (e) {
          if (document.body.contains(container)) document.body.removeChild(container);
        }
      }

      if (returnBlob) return doc.output("blob");
      doc.save(`${(report.reportTitle || "report").replace(/\s+/g, "_")}.pdf`);
      return true;
    } catch (err) {
      console.error("Multi-sheet PDF export failed:", err);
      return false;
    }
  }

  try {
    const result = await buildPatchedBytes(report, template, rawBytes);
    if (!result) return false;

    // Find which cells are input cells (yellow in the original template)
    const inputCells = getInputCellSet(rawBytes, template.name);

    // Parse the patched workbook to get cell data + column widths
    const wb = XLSX.read(result.bytes, { type: "array", cellStyles: true, cellFormula: true });
    const ws = wb.Sheets[template.name] || wb.Sheets[wb.SheetNames[0]];
    if (ws) evalCellFormulas(ws, wb);
    if (!ws || !ws["!ref"]) return false;

    const fullRange = XLSX.utils.decode_range(ws["!ref"]);

    // Trim to the last row/col that actually has content — Excel !ref often spans
    // thousands of empty rows, which would make the PDF mostly blank space.
    let lastR = fullRange.s.r, lastC = fullRange.s.c;
    for (const key of Object.keys(ws)) {
      if (key.startsWith("!")) continue;
      const cell = ws[key];
      // Include cells that have a value OR a visible style (background fill)
      const hasContent = cell && (cell.v !== undefined || cell.f || cell.s?.fgColor);
      if (!hasContent) continue;
      const decoded = XLSX.utils.decode_cell(key);
      if (decoded.r > lastR) lastR = decoded.r;
      if (decoded.c > lastC) lastC = decoded.c;
    }
    // Small padding so the last row of data isn't clipped
    const range = { s: fullRange.s, e: { r: Math.min(lastR + 2, fullRange.e.r), c: Math.min(lastC + 1, fullRange.e.c) } };

    // Build column widths (in px, ~7px per Excel character unit)
    const colWidths = [];
    const colInfos = ws["!cols"] || [];
    for (let c = range.s.c; c <= range.e.c; c++) {
      const ci = colInfos[c];
      colWidths.push(ci?.wpx ? ci.wpx : ci?.wch ? Math.round(ci.wch * 7) : 64);
    }

    // Build row heights (in px, ~1.33px per Excel point)
    const rowHeights = [];
    const rowInfos = ws["!rows"] || [];
    for (let r = range.s.r; r <= range.e.r; r++) {
      const ri = rowInfos[r];
      rowHeights.push(ri?.hpx ? ri.hpx : ri?.hpt ? Math.round(ri.hpt * 1.33) : 20);
    }

    // Build merge lookup: "r,c" → { rowspan, colspan }
    const mergeMap = {};
    const hiddenCells = new Set();
    (ws["!merges"] || []).forEach(m => {
      // Only include merges within our trimmed range
      if (m.s.r > range.e.r || m.s.c > range.e.c) return;
      mergeMap[`${m.s.r},${m.s.c}`] = { rowspan: m.e.r - m.s.r + 1, colspan: m.e.c - m.s.c + 1 };
      for (let mr = m.s.r; mr <= m.e.r; mr++) {
        for (let mc = m.s.c; mc <= m.e.c; mc++) {
          if (mr !== m.s.r || mc !== m.s.c) hiddenCells.add(`${mr},${mc}`);
        }
      }
    });

    // Excel indexed color palette (indices used by this template type)
    const INDEXED_COLORS = {
      42: "#CCFFCC", 43: "#FFFF99", 44: "#FFCC99", 45: "#FF99CC",
      12: "#800080", 16: "#9999FF",
    };

    // Helper: true if the row has visible content worth rendering.
    // If a row contains input cells (originally yellow), it's a data row and is only
    // shown when at least one input cell has a user-entered value.
    // Rows with no input cells (headers, labels, totals) are shown based on static content
    // or non-zero formula results.
    const rowHasContent = (r) => {
      let hasInputCell = false;
      let anyInputFilled = false;
      let hasStaticContent = false;
      for (let c = range.s.c; c <= range.e.c; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        const cell = ws[addr];
        if (!cell) continue;
        if (inputCells.has(addr)) {
          hasInputCell = true;
          if (cell.v !== undefined && cell.v !== null && cell.v !== "") anyInputFilled = true;
        } else if (!cell.f) {
          if (cell.v !== undefined && cell.v !== null && cell.v !== "") hasStaticContent = true;
        }
      }
      if (hasInputCell) return anyInputFilled;
      if (hasStaticContent) return true;
      for (let c = range.s.c; c <= range.e.c; c++) {
        const cell = ws[XLSX.utils.encode_cell({ r, c })];
        if (cell?.f && cell.v !== undefined && cell.v !== null && cell.v !== "" && cell.v !== 0) return true;
      }
      return false;
    };

    const isYellowFillPdf = (hex) => hex && hex.toUpperCase().slice(-6) === "FFFBCC";
    const isNearWhitePdf = (hex) => {
      if (!hex) return true;
      const h = hex.toUpperCase().slice(-6);
      const rr = parseInt(h.slice(0,2),16), gg = parseInt(h.slice(2,4),16), bb = parseInt(h.slice(4,6),16);
      return (0.299*rr + 0.587*gg + 0.114*bb)/255 > 0.92;
    };
    const getCellBgHexPdf = (cell) => {
      if (!cell?.s?.fgColor) return null;
      const fc = cell.s.fgColor;
      if (fc.rgb) return fc.rgb.slice(-6);
      if (fc.indexed !== undefined && INDEXED_COLORS[fc.indexed]) return INDEXED_COLORS[fc.indexed].replace("#","");
      if (fc.theme !== undefined) return resolveThemeColor(fc.theme, fc.tint).replace("#","");
      return null;
    };
    const isRowSectionBarPdf = (r) => {
      let hasContent = false;
      for (let c = range.s.c; c <= range.e.c; c++) {
        const key = `${r},${c}`;
        if (hiddenCells.has(key)) continue;
        const cell = ws[XLSX.utils.encode_cell({ r, c })];
        if (!cell || (cell.v === undefined || cell.v === null || cell.v === "") && !cell.f) continue;
        hasContent = true;
        if (!isNearBlack(getCellBgHexPdf(cell))) return false;
      }
      return hasContent;
    };
    // Mirrors getSectionBarInfo in the preview: a bar row can carry more than one
    // title when blocks sit side by side, and each is rendered over its own span.
    const getSectionBarInfoPdf = (r) => {
      let bg = "#373737";
      const found = [];
      for (let c = range.s.c; c <= range.e.c; c++) {
        const key = `${r},${c}`;
        if (hiddenCells.has(key)) continue;
        const cell = ws[XLSX.utils.encode_cell({ r, c })];
        if (!cell) continue;
        const bgHex = getCellBgHexPdf(cell);
        if (bgHex && bg === "#373737") bg = `#${bgHex}`;
        const raw = cell.w !== undefined ? cell.w : (cell.v !== undefined && cell.v !== null ? String(cell.v) : "");
        if (raw) found.push({ text: raw, c });
      }
      const segs = found.map((f, i) => {
        const endC = i + 1 < found.length ? found[i + 1].c - 1 : range.e.c;
        let width = 0;
        for (let c = f.c; c <= endC; c++) width += colWidths[c - range.s.c] || 0;
        return { text: f.text, width: Math.max(width, 1) };
      });
      return { text: found.length ? found[0].text : "", bg, segs };
    };
    // Columns the author hid in Excel. Hiding a helper column is how a spreadsheet
    // keeps its machinery off the printed page, and rendering it anyway puts working
    // values into a client-facing report.
    const hiddenCols = new Set();
    (ws["!cols"] || []).forEach((ci, i) => { if (ci && ci.hidden) hiddenCols.add(i); });

    const renderRowPdf = (r, rowIdx) => {
      const rh = rowHeights[r - range.s.r];
      let tr = `<tr style="height:${rh}px">`;
      for (let c = range.s.c; c <= range.e.c; c++) {
        if (hiddenCols.has(c)) continue;
        const key = `${r},${c}`;
        if (hiddenCells.has(key)) continue;
        const addr = XLSX.utils.encode_cell({ r, c });
        const cell = ws[addr];
        const merge = mergeMap[key];
        const rsAttr = merge?.rowspan > 1 ? ` rowspan="${merge.rowspan}"` : "";
        const csAttr = merge?.colspan > 1 ? ` colspan="${merge.colspan}"` : "";
        const s = cell?.s;
        const bgHexPdf = getCellBgHexPdf(cell);
        const cellIsDarkPdf = isDarkHex(bgHexPdf);
        const cellHasMeaningfulFillPdf = bgHexPdf && !cellIsDarkPdf && !isYellowFillPdf(bgHexPdf) && !isNearWhitePdf(bgHexPdf);
        const rowEvenPdf = rowIdx % 2 === 0;
        const bgStyle = (cellIsDarkPdf || cellHasMeaningfulFillPdf)
          ? `background:#${bgHexPdf};`
          : `background:${rowEvenPdf ? "#ffffff" : "#f8f8f8"};`;
        let cellStyle = `border:1px solid #e2e8f0;padding:4px 8px;overflow:hidden;height:${rh}px;${bgStyle}`;
        if (cellIsDarkPdf) {
          cellStyle += "color:#ffffff;font-weight:bold;text-align:center;";
        } else {
          cellStyle += "color:#111111;";
          if (cellHasMeaningfulFillPdf) {
            cellStyle += "font-weight:bold;";
            if (!s?.alignment?.horizontal) cellStyle += "text-align:center;";
          }
        }
        if (s?.font?.bold) cellStyle += "font-weight:bold;";
        if (s?.font?.italic) cellStyle += "font-style:italic;";
        if (s?.alignment?.horizontal === "center") cellStyle += "text-align:center;";
        else if (s?.alignment?.horizontal === "right") cellStyle += "text-align:right;";
        let cellText = "";
        if (cell) {
          const raw = cell.w !== undefined ? cell.w : (cell.v !== undefined && cell.v !== null ? String(cell.v) : "");
          cellText = /^#(VALUE|REF|DIV\/0!|N\/A|NAME\?|NUM!|NULL!)/.test(raw) ? "" : raw;
        }
        tr += `<td${rsAttr}${csAttr} style="${cellStyle}">${cellText.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")}</td>`;
      }
      return tr + "</tr>";
    };

    const reportTitlePdf = (report?.reportTitle || template?.name || "Report");
    const totalWidthPdf = colWidths.reduce((s, w) => s + w, 0);
    const visibleWidthsPdf = colWidths.filter((_, i) => !hiddenCols.has(range.s.c + i));
    const colgroupPdf = "<colgroup>" + visibleWidthsPdf.map(w => `<col style="width:${w}px">`).join("") + "</colgroup>";
    const tableBasePdf = `border-collapse:collapse;font-family:Arial,sans-serif;font-size:11px;table-layout:fixed;width:${totalWidthPdf}px;`;

    // Group visible rows into sections (dark-bar rows become section header divs)
    const sectionsPdf = [];
    let curPdf = { barRow: null, dataRows: [] };
    for (let r = range.s.r; r <= range.e.r; r++) {
      if (!rowHasContent(r)) continue;
      if (isRowSectionBarPdf(r)) {
        if (curPdf.barRow !== null || curPdf.dataRows.length > 0) sectionsPdf.push(curPdf);
        curPdf = { barRow: r, dataRows: [] };
      } else {
        curPdf.dataRows.push(r);
      }
    }
    if (curPdf.barRow !== null || curPdf.dataRows.length > 0) sectionsPdf.push(curPdf);

    let sectionsHtmlPdf = "";
    for (const sec of sectionsPdf) {
      let secHtml = "";
      if (sec.barRow !== null) {
        const { text, bg, segs } = getSectionBarInfoPdf(sec.barRow);
        const escBar = (t) => t.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
        const barStyle = `background:${bg};color:#fff;font-weight:700;font-size:11px;text-align:center;padding:5px 8px;letter-spacing:0.5px;`;
        if (segs && segs.length > 1) {
          const parts = segs.map((sg, i) =>
            `<div style="${barStyle}flex:${sg.width} 1 0;${i ? "border-left:1px solid rgba(255,255,255,0.35);" : ""}">${escBar(sg.text)}</div>`
          ).join("");
          secHtml += `<div style="display:flex;width:100%;">${parts}</div>`;
        } else {
          secHtml += `<div style="${barStyle}">${escBar(text)}</div>`;
        }
      }
      if (sec.dataRows.length > 0) {
        const rows = sec.dataRows.map((r, i) => renderRowPdf(r, i)).join("");
        secHtml += `<table style="${tableBasePdf}">${colgroupPdf}<tbody>${rows}</tbody></table>`;
      }
      sectionsHtmlPdf += `<div style="margin-bottom:14px;">${secHtml}</div>`;
    }

    const escPdf = (s) => s.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
    let tableHtml =
      `<div style="background:#fff;font-family:Arial,sans-serif;overflow:hidden;">` +
      `<div style="background:#282828;color:#fff;font-weight:700;font-size:13px;padding:8px 14px;">${escPdf(reportTitlePdf)}</div>` +
      `<div style="padding:12px 12px 4px;">${sectionsHtmlPdf}</div>` +
      `</div>`;

    // Render off-screen — full natural width so html2canvas captures real dimensions
    const container = document.createElement("div");
    container.style.cssText = "position:fixed;top:-9999px;left:-9999px;background:white;padding:8px;width:max-content;";
    container.innerHTML = tableHtml;
    document.body.appendChild(container);

    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        width: container.scrollWidth,
        height: container.scrollHeight,
      });
      document.body.removeChild(container);

      const imgW = canvas.width / 2;   // undo the 2× scale
      const imgH = canvas.height / 2;

      // Use landscape if the sheet is wider than tall
      const landscape = imgW > imgH;
      const doc = new jsPDFClass({ unit: "pt", format: "letter", orientation: landscape ? "landscape" : "portrait" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 18;
      const availW = pageW - margin * 2;
      const availH = pageH - margin * 2;

      // Scale to fill the page width; split vertically across pages for tall sheets
      const scaleX = availW / imgW;
      const drawW = availW;
      const totalDrawH = imgH * scaleX;
      const pagesNeeded = Math.ceil(totalDrawH / availH);

      for (let p = 0; p < pagesNeeded; p++) {
        if (p > 0) doc.addPage();
        // Source slice in original (non-scaled) canvas coordinates
        const srcYStart = (p * availH / scaleX) * 2;   // ×2 for canvas scale
        const srcSliceH = Math.min((availH / scaleX) * 2, canvas.height - srcYStart);
        const destDrawH = (srcSliceH / 2) * scaleX;    // destination height in pt

        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = srcSliceH;
        const ctx = sliceCanvas.getContext("2d");
        ctx.drawImage(canvas, 0, srcYStart, canvas.width, srcSliceH, 0, 0, canvas.width, srcSliceH);
        doc.addImage(sliceCanvas.toDataURL("image/jpeg", 0.92), "JPEG", margin, margin, drawW, destDrawH);
      }

      // Append embedded chart images. The multi-sheet branch above does this per
      // sheet; a single-sheet template has only the one, so take every image passed
      // in rather than matching on sheet name, which avoids a mismatch when the
      // template name and the worksheet name differ.
      for (const dataUrl of Object.values(chartImagesBySheet).flat().filter(Boolean)) {
        doc.addPage();
        const chartH = Math.round(availW * (350 / 800));
        doc.addImage(dataUrl, "PNG", margin, margin, availW, chartH);
      }

      if (returnBlob) return doc.output("blob");
      const filename = `${(report.reportTitle || "report").replace(/\s+/g, "_")}.pdf`;
      doc.save(filename);
      return true;
    } catch (e) {
      document.body.removeChild(container);
      throw e;
    }
  } catch (err) {
    console.error("Excel PDF export failed:", err);
    return false;
  }
}

// ─── Public: build HTML table string for in-app preview ──────────────────────
// Returns an HTML string (the rendered table) or null on failure.
// Same rendering logic as exportReportAsExcelPDF but without the canvas/PDF step.
export async function buildExcelPreviewHtml(report, template, rawBytes) {
  if (!rawBytes) return null;
  try {
    const result = await buildPatchedBytes(report, template, rawBytes);
    if (!result) return null;

    // Find which cells are input cells (yellow in the original template)
    const previewSheetName = template.name || "";
    const inputCells = getInputCellSet(rawBytes, previewSheetName);
    const rowFillMap = await getRowFillMap(rawBytes, previewSheetName);

    const wb = XLSX.read(result.bytes, { type: "array", cellStyles: true, cellFormula: true });
    const ws = wb.Sheets[previewSheetName] || wb.Sheets[wb.SheetNames[0]];
    if (!ws || !ws["!ref"]) return null;
    evalCellFormulas(ws, wb);

    const fullRange = XLSX.utils.decode_range(ws["!ref"]);
    // Trust !ref when the sheet has input cells (row-level styled empty rows don't appear
    // as <c> elements, so content scanning would stop too early)
    const range = inputCells.size > 0
      ? { s: fullRange.s, e: { r: fullRange.e.r, c: fullRange.e.c } }
      : (() => {
          let lastR = fullRange.s.r, lastC = fullRange.s.c;
          for (const key of Object.keys(ws)) {
            if (key.startsWith("!")) continue;
            const cell = ws[key];
            if (!cell || (cell.v === undefined && !cell.f)) continue;
            const decoded = XLSX.utils.decode_cell(key);
            if (decoded.r > lastR) lastR = decoded.r;
            if (decoded.c > lastC) lastC = decoded.c;
          }
          return { s: fullRange.s, e: { r: lastR, c: lastC } };
        })();

    const colWidths = [];
    const colInfos = ws["!cols"] || [];
    for (let c = range.s.c; c <= range.e.c; c++) {
      const ci = colInfos[c];
      colWidths.push(ci?.wpx ? ci.wpx : ci?.wch ? Math.round(ci.wch * 7) : 64);
    }

    const rowHeights = [];
    const rowInfos = ws["!rows"] || [];
    for (let r = range.s.r; r <= range.e.r; r++) {
      const ri = rowInfos[r];
      rowHeights.push(ri?.hpx ? ri.hpx : ri?.hpt ? Math.round(ri.hpt * 1.33) : 20);
    }

    const mergeMap = {};
    const hiddenCells = new Set();
    (ws["!merges"] || []).forEach(m => {
      if (m.s.r > range.e.r || m.s.c > range.e.c) return;
      mergeMap[`${m.s.r},${m.s.c}`] = { rowspan: m.e.r - m.s.r + 1, colspan: m.e.c - m.s.c + 1 };
      for (let mr = m.s.r; mr <= m.e.r; mr++) {
        for (let mc = m.s.c; mc <= m.e.c; mc++) {
          if (mr !== m.s.r || mc !== m.s.c) hiddenCells.add(`${mr},${mc}`);
        }
      }
    });

    const INDEXED_COLORS = {
      42: "#CCFFCC", 43: "#FFFF99", 44: "#FFCC99", 45: "#FF99CC",
      12: "#800080", 16: "#9999FF",
    };

    // True if a fill color is yellow input color (treat as no fill in preview)
    const isYellowFill = (hex) => hex && hex.toUpperCase().slice(-6) === "FFFBCC";
    const isInputFill = (hex) => isYellowFill(hex) || isRedFill(hex); // yellow or red = input cell
    // True if a fill color is white/near-white (no meaningful fill)
    const isNearWhite = (hex) => {
      if (!hex) return true;
      const h = hex.toUpperCase().slice(-6);
      const r2 = parseInt(h.slice(0,2),16), g2 = parseInt(h.slice(2,4),16), b2 = parseInt(h.slice(4,6),16);
      return (0.299*r2 + 0.587*g2 + 0.114*b2)/255 > 0.92;
    };

    const rowHasContentPrev = (r) => {
      let hasInputCell = false;
      let anyInputFilled = false;
      let hasStaticContent = false;
      for (let c = range.s.c; c <= range.e.c; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        const cell = ws[addr];
        if (!cell) continue;
        if (inputCells.has(addr)) {
          hasInputCell = true;
          if (cell.v !== undefined && cell.v !== null && cell.v !== "") anyInputFilled = true;
        } else if (!cell.f) {
          if (cell.v !== undefined && cell.v !== null && cell.v !== "") hasStaticContent = true;
        }
      }
      if (hasInputCell) return anyInputFilled;
      if (hasStaticContent) return true;
      for (let c = range.s.c; c <= range.e.c; c++) {
        const cell = ws[XLSX.utils.encode_cell({ r, c })];
        if (cell?.f && cell.v !== undefined && cell.v !== null && cell.v !== "" && cell.v !== 0) return true;
      }
      return false;
    };

    const totalWidth = colWidths.reduce((s, w) => s + w, 0);
    const reportTitle = (report?.reportTitle || template?.name || "Report");

    // Helper: resolve background hex for a cell
    const getCellBgHex = (cell, rowIdx) => {
      if (cell?.s?.fgColor) {
        const fc = cell.s.fgColor;
        if (fc.rgb) return fc.rgb.slice(-6);
        if (fc.indexed !== undefined && INDEXED_COLORS[fc.indexed]) return INDEXED_COLORS[fc.indexed].replace("#","");
        if (fc.theme !== undefined) return resolveThemeColor(fc.theme, fc.tint).replace("#","");
      }
      // Fallback: row-level fill (SheetJS doesn't inherit row styles to cells)
      if (rowIdx !== undefined && rowFillMap.has(rowIdx)) return rowFillMap.get(rowIdx).replace("#","");
      return null;
    };

    // A row is a section bar if every non-empty cell has a near-black fill.
    // Requires truly black/near-black fills (luminance < 0.20) so dark greys and blues
    // used as column headers don't accidentally trigger section breaks.
    const isRowSectionBar = (r) => {
      let hasContent = false;
      for (let c = range.s.c; c <= range.e.c; c++) {
        const key = `${r},${c}`;
        if (hiddenCells.has(key)) continue;
        const cell = ws[XLSX.utils.encode_cell({ r, c })];
        if (!cell || (cell.v === undefined || cell.v === null || cell.v === "") && !cell.f) continue;
        hasContent = true;
        if (!isNearBlack(getCellBgHex(cell, r))) return false;
      }
      return hasContent;
    };

    // Section bar contents. A bar row can carry more than one title when blocks sit
    // side by side, so every text on the row is returned with the columns it covers,
    // and each is rendered over its own span. Taking only the first text made one
    // title span the full width and silently dropped the other.
    const getSectionBarInfo = (r) => {
      let bg = "#373737";
      const found = [];
      for (let c = range.s.c; c <= range.e.c; c++) {
        const key = `${r},${c}`;
        if (hiddenCells.has(key)) continue;
        const cell = ws[XLSX.utils.encode_cell({ r, c })];
        if (!cell) continue;
        const bgHex = getCellBgHex(cell, r);
        if (bgHex && bg === "#373737") bg = `#${bgHex}`;
        const raw = cell.w !== undefined ? cell.w : (cell.v !== undefined && cell.v !== null ? String(cell.v) : "");
        if (raw) found.push({ text: raw, c });
      }
      // Each title runs until the next one starts, so the segments tile the row.
      const segs = found.map((f, i) => {
        const endC = i + 1 < found.length ? found[i + 1].c - 1 : range.e.c;
        let width = 0;
        for (let c = f.c; c <= endC; c++) width += colWidths[c - range.s.c] || 0;
        return { text: f.text, width: Math.max(width, 1) };
      });
      return { text: found.length ? found[0].text : "", bg, segs };
    };

    // Find the rightmost column that has actual content (value or formula) in a set of rows
    const getSectionMaxC = (dataRows) => {
      let maxC = range.s.c;
      for (const r of dataRows) {
        for (let c = range.s.c; c <= range.e.c; c++) {
          if (hiddenCells.has(`${r},${c}`)) continue;
          const cell = ws[XLSX.utils.encode_cell({ r, c })];
          if (!cell) continue;
          if ((cell.v !== undefined && cell.v !== null && cell.v !== "") || cell.f) {
            if (c > maxC) maxC = c;
          }
        }
      }
      return maxC;
    };

    // Render a single data row as a <tr> string, capped at maxC
    // Columns the author hid in Excel. Hiding a helper column is how a spreadsheet
    // keeps its machinery off the printed page, and rendering it anyway puts working
    // values into a client-facing report.
    const hiddenCols = new Set();
    (ws["!cols"] || []).forEach((ci, i) => { if (ci && ci.hidden) hiddenCols.add(i); });

    const renderRow = (r, rowIdx, maxC = range.e.c) => {
      const rh = rowHeights[r - range.s.r];
      let tr = `<tr style="height:${rh}px">`;
      for (let c = range.s.c; c <= maxC; c++) {
        if (hiddenCols.has(c)) continue;
        const key = `${r},${c}`;
        if (hiddenCells.has(key)) continue;
        const addr = XLSX.utils.encode_cell({ r, c });
        const cell = ws[addr];
        const merge = mergeMap[key];
        const rsAttr = merge?.rowspan > 1 ? ` rowspan="${merge.rowspan}"` : "";
        const csAttr = merge?.colspan > 1 ? ` colspan="${merge.colspan}"` : "";
        const s = cell?.s;
        const bgHex = getCellBgHex(cell, r);
        const cellIsDark = isDarkHex(bgHex);
        const cellHasMeaningfulFill = bgHex && !cellIsDark && !isInputFill(bgHex) && !isNearWhite(bgHex);
        const rowEven = rowIdx % 2 === 0;
        const bgStyle = (cellIsDark || cellHasMeaningfulFill)
          ? `background:#${bgHex};`
          : `background:${rowEven ? "#ffffff" : "#f8f8f8"};`;
        let cellStyle = `border:1px solid #e2e8f0;padding:4px 8px;overflow:hidden;height:${rh}px;${bgStyle}`;
        if (cellIsDark) {
          cellStyle += "color:#ffffff;font-weight:bold;text-align:center;";
        } else {
          cellStyle += "color:#111111;";
          if (cellHasMeaningfulFill) {
            cellStyle += "font-weight:bold;";
            if (!s?.alignment?.horizontal) cellStyle += "text-align:center;";
          }
        }
        if (s?.font?.bold) cellStyle += "font-weight:bold;";
        if (s?.font?.italic) cellStyle += "font-style:italic;";
        if (s?.alignment?.horizontal === "center") cellStyle += "text-align:center;";
        else if (s?.alignment?.horizontal === "right") cellStyle += "text-align:right;";
        let cellText = "";
        if (cell) {
          const raw = cell.w !== undefined ? cell.w : (cell.v !== undefined && cell.v !== null ? String(cell.v) : "");
          cellText = /^#(VALUE|REF|DIV\/0!|N\/A|NAME\?|NUM!|NULL!)/.test(raw) ? "" : raw;
        }
        tr += `<td${rsAttr}${csAttr} style="${cellStyle}">${cellText.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")}</td>`;
      }
      return tr + "</tr>";
    };

    // Classify and group all visible rows into sections.
    // A new section starts whenever we hit a "section bar" row (all cells dark).
    const sections = [];
    let cur = { barRow: null, dataRows: [] };
    for (let r = range.s.r; r <= range.e.r; r++) {
      if (!rowHasContentPrev(r)) continue;
      if (isRowSectionBar(r)) {
        if (cur.barRow !== null || cur.dataRows.length > 0) sections.push(cur);
        cur = { barRow: r, dataRows: [] };
      } else {
        cur.dataRows.push(r);
      }
    }
    if (cur.barRow !== null || cur.dataRows.length > 0) sections.push(cur);

    // Render each section: dark bar div + data table, separated by whitespace.
    // Each section gets its own colgroup trimmed to the section's actual content width.
    let sectionsHtml = "";
    for (const sec of sections) {
      let secHtml = "";
      if (sec.barRow !== null) {
        const { text, bg, segs } = getSectionBarInfo(sec.barRow);
        const esc0 = (t) => t.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
        const barStyle = `background:${bg};color:#fff;font-weight:700;font-size:11px;text-align:center;padding:5px 8px;letter-spacing:0.5px;`;
        if (segs && segs.length > 1) {
          const total = segs.reduce((a, sg) => a + sg.width, 0);
          const parts = segs.map((sg, i) =>
            `<div style="${barStyle}flex:${sg.width} 1 0;${i ? "border-left:1px solid rgba(255,255,255,0.35);" : ""}">${esc0(sg.text)}</div>`
          ).join("");
          secHtml += `<div style="display:flex;width:100%;" data-bar-cols="${total}">${parts}</div>`;
        } else {
          secHtml += `<div style="${barStyle}">${esc0(text)}</div>`;
        }
      }
      if (sec.dataRows.length > 0) {
        const secMaxC = getSectionMaxC(sec.dataRows);
        const secWidths = [];
        for (let c = range.s.c; c <= secMaxC; c++) {
          if (hiddenCols.has(c)) continue;
          secWidths.push(colWidths[c - range.s.c]);
        }
        const secTotalWidth = secWidths.reduce((s, w) => s + w, 0);
        const secColgroup = "<colgroup>" + secWidths.map(w => `<col style="width:${w}px">`).join("") + "</colgroup>";
        const secTableBase = `border-collapse:collapse;font-family:Arial,sans-serif;font-size:11px;table-layout:fixed;width:${secTotalWidth}px;`;
        const rows = sec.dataRows.map((r, i) => renderRow(r, i, secMaxC)).join("");
        secHtml += `<div style="overflow-x:auto;"><table style="${secTableBase}">${secColgroup}<tbody>${rows}</tbody></table></div>`;
      }
      sectionsHtml += `<div style="margin-bottom:14px;">${secHtml}</div>`;
    }

    const esc = (s) => s.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
    const html =
      `<div style="background:#fff;max-width:${Math.max(totalWidth + 26, 600)}px;margin:0 auto;box-shadow:0 2px 16px rgba(0,0,0,0.13);border-radius:4px;overflow:hidden;font-family:Arial,sans-serif;">` +
      `<div style="background:#282828;color:#fff;font-weight:700;font-size:13px;padding:8px 14px;">${esc(reportTitle)}</div>` +
      `<div style="padding:12px 12px 4px;">${sectionsHtml}</div>` +
      `</div>`;
    return html;
  } catch (err) {
    console.error("Excel preview build failed:", err);
    return null;
  }
}

// ─── Public: build editable HTML table for the report edit form ───────────────
// Like buildExcelPreviewHtml but input cells (matching a template field _addr)
// become <input> elements with data-field-id attributes for event delegation.
export async function buildExcelEditableHtml(template, rawBytes) {
  if (!rawBytes) return null;
  try {
    // Parse the unpatched workbook so we see the original labels/structure
    const wb = XLSX.read(rawBytes, { type: "array", cellStyles: true, cellFormula: true });
    const sheetName = (template.name && wb.Sheets[template.name]) ? template.name : wb.SheetNames[0];
    const ws = wb.Sheets[sheetName];
    if (!ws || !ws["!ref"]) return null;

    // Row-level fill fallback: SheetJS doesn't inherit row-level styles to cells
    // that have no explicit cell-level style. Read row fills from raw XML.
    const rowFillMap = await getRowFillMap(rawBytes, sheetName);

    // Build addr → fieldId map and addr → required map
    const addrToFieldId = {};
    const addrToRequired = {};
    for (const sec of template.sections || []) {
      for (const field of sec.fields || []) {
        if (field._addr) {
          addrToFieldId[field._addr] = field.id;
          if (field.required) addrToRequired[field._addr] = true;
        }
      }
    }

    const fullRange = XLSX.utils.decode_range(ws["!ref"]);
    const hasInputFields = Object.keys(addrToFieldId).length > 0;
    let lastR, lastC;

    // Always scan cells for content bounds
    let contentLastR = fullRange.s.r, contentLastC = fullRange.s.c;
    for (const key of Object.keys(ws)) {
      if (key.startsWith("!")) continue;
      const cell = ws[key];
      if (!cell || (cell.v === undefined && !cell.f)) continue;
      const decoded = XLSX.utils.decode_cell(key);
      if (decoded.r > contentLastR) contentLastR = decoded.r;
      if (decoded.c > contentLastC) contentLastC = decoded.c;
    }

    if (hasInputFields) {
      // Cap to max(last field addr row, last content row) + 2 buffer.
      // Empty editable rows (yellow row fill, no <c> element) have their row index
      // covered by the field _addr, so maxFieldRow captures them.
      // This prevents rendering hundreds of blank rows when !ref extends far
      // (e.g. Proctor sheet whose !ref is pushed out by chart/formula areas).
      let maxFieldRow = fullRange.s.r, maxFieldCol = fullRange.s.c;
      for (const addr of Object.keys(addrToFieldId)) {
        try {
          const decoded = XLSX.utils.decode_cell(addr);
          if (decoded.r > maxFieldRow) maxFieldRow = decoded.r;
          if (decoded.c > maxFieldCol) maxFieldCol = decoded.c;
        } catch {}
      }
      lastR = Math.min(fullRange.e.r, Math.max(maxFieldRow, contentLastR) + 2);
      lastC = Math.min(fullRange.e.c, Math.max(maxFieldCol, contentLastC) + 1);
    } else {
      lastR = contentLastR;
      lastC = contentLastC;
    }
    const range = { s: fullRange.s, e: { r: lastR, c: lastC } };

    const colWidths = [];
    const colInfos = ws["!cols"] || [];
    for (let c = range.s.c; c <= range.e.c; c++) {
      const ci = colInfos[c];
      colWidths.push(ci?.wpx ? ci.wpx : ci?.wch ? Math.round(ci.wch * 7) : 64);
    }

    const rowHeights = [];
    const rowInfos = ws["!rows"] || [];
    for (let r = range.s.r; r <= range.e.r; r++) {
      const ri = rowInfos[r];
      rowHeights.push(ri?.hpx ? ri.hpx : ri?.hpt ? Math.round(ri.hpt * 1.33) : 20);
    }

    const mergeMap = {};
    const hiddenCells = new Set();
    (ws["!merges"] || []).forEach(m => {
      if (m.s.r > range.e.r || m.s.c > range.e.c) return;
      mergeMap[`${m.s.r},${m.s.c}`] = { rowspan: m.e.r - m.s.r + 1, colspan: m.e.c - m.s.c + 1 };
      for (let mr = m.s.r; mr <= m.e.r; mr++) {
        for (let mc = m.s.c; mc <= m.e.c; mc++) {
          if (mr !== m.s.r || mc !== m.s.c) hiddenCells.add(`${mr},${mc}`);
        }
      }
    });

    const INDEXED_COLORS = {
      42: "#CCFFCC", 43: "#FFFF99", 44: "#FFCC99", 45: "#FF99CC",
      12: "#800080", 16: "#9999FF",
    };

    let html = `<table style="border-collapse:collapse;font-family:Calibri,Arial,sans-serif;font-size:11px;table-layout:fixed;white-space:nowrap;color:#111111;">`;
    html += "<colgroup>";
    for (let c = range.s.c; c <= range.e.c; c++) {
      html += `<col style="width:${colWidths[c - range.s.c]}px">`;
    }
    html += "</colgroup>";

    for (let r = range.s.r; r <= range.e.r; r++) {
      const rh = rowHeights[r - range.s.r];
      html += `<tr style="height:${rh}px">`;
      for (let c = range.s.c; c <= range.e.c; c++) {
        const key = `${r},${c}`;
        if (hiddenCells.has(key)) continue;
        const addr = XLSX.utils.encode_cell({ r, c });
        const cell = ws[addr];
        const merge = mergeMap[key];
        const rsAttr = merge?.rowspan > 1 ? ` rowspan="${merge.rowspan}"` : "";
        const csAttr = merge?.colspan > 1 ? ` colspan="${merge.colspan}"` : "";

        const s = cell?.s;
        let bg = "";
        if (s?.fgColor) {
          if (s.fgColor.rgb) bg = `#${s.fgColor.rgb.slice(-6)}`;
          else if (s.fgColor.indexed !== undefined && INDEXED_COLORS[s.fgColor.indexed]) bg = INDEXED_COLORS[s.fgColor.indexed];
          else if (s.fgColor.theme !== undefined) bg = resolveThemeColor(s.fgColor.theme, s.fgColor.tint);
        }
        // Fallback: if cell has no fill, inherit the row-level fill (SheetJS doesn't do this)
        if (!bg && rowFillMap.has(r)) bg = rowFillMap.get(r);

        let cellStyle = `border:0.5px solid #d0d0d0;padding:0;overflow:hidden;height:${rh}px;`;
        if (bg) cellStyle += `background:${bg};`;
        if (isDarkHex(bg.replace("#", ""))) cellStyle += "color:#ffffff;";
        if (s?.font?.bold) cellStyle += "font-weight:bold;";
        if (s?.font?.italic) cellStyle += "font-style:italic;";
        const align = s?.alignment?.horizontal === "center" ? "center" : s?.alignment?.horizontal === "right" ? "right" : "left";

        const fieldId = addrToFieldId[addr];
        if (fieldId) {
          const isReq = addrToRequired[addr];
          if (isReq) {
            // Required cell — pink/red background with red border
            cellStyle += `background:#ffe4e4;border:1.5px solid #f87171;box-shadow:inset 0 0 0 1px #fecaca;color:#111;`;
          } else {
            // Optional input cell — yellow background
            cellStyle += `background:#fffbcc;border:1.5px solid #a5b4fc;box-shadow:inset 0 0 0 1px #e0e7ff;color:#111;`;
          }
          const inputStyle = `width:100%;height:100%;border:none;background:transparent;font-size:11px;font-family:Calibri,Arial,sans-serif;color:#111;padding:2px 3px;box-sizing:border-box;text-align:${align};outline:none;cursor:text;`;
          html += `<td${rsAttr}${csAttr} style="${cellStyle}"><input data-field-id="${fieldId}" data-required="${isReq ? "1" : "0"}" style="${inputStyle}" /></td>`;
        } else {
          let cellText = cell ? (cell.w !== undefined ? cell.w : (cell.v !== undefined && cell.v !== null ? String(cell.v) : "")) : "";
          cellStyle += `padding:2px 3px;text-align:${align};`;
          const formulaAttr = cell?.f ? ` data-formula-addr="${addr}"` : "";
          html += `<td${rsAttr}${csAttr}${formulaAttr} style="${cellStyle}">${cellText.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</td>`;
        }
      }
      html += "</tr>";
    }
    html += "</table>";
    return html;
  } catch (err) {
    console.error("Excel editable build failed:", err);
    return null;
  }
}

// ─── Public: export filled report as Excel clone ─────────────────────────────
export function exportReportAsExcelClone(report, template, originalWorkbook) {
  if (!originalWorkbook) { exportGeneric(report, template); return; }
  try {
    const wb = { SheetNames: [...originalWorkbook.SheetNames], Sheets: {}, Props: originalWorkbook.Props };
    originalWorkbook.SheetNames.forEach(name => {
      const ws = originalWorkbook.Sheets[name];
      const cloned = {};
      Object.keys(ws).forEach(k => {
        if (k.startsWith("!")) {
          cloned[k] = Array.isArray(ws[k]) ? ws[k].map(v => typeof v === "object" && v ? { ...v } : v) : ws[k];
        } else {
          cloned[k] = { ...ws[k] };
        }
      });
      wb.Sheets[name] = cloned;
    });

    const sheetName = originalWorkbook.SheetNames.find(n => n === template.name) || originalWorkbook.SheetNames[0];
    const ws = wb.Sheets[sheetName];
    if (!ws?.["!ref"]) { exportGeneric(report, template); return; }
    const range = XLSX.utils.decode_range(ws["!ref"]);

    // Build label → address map
    const labelToAddr = {};
    for (let r = range.s.r; r <= range.e.r; r++) {
      for (let c = range.s.c; c <= range.e.c; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        const cell = ws[addr];
        if (!cell || cell.f) continue;
        if (typeof cell.v === "string") {
          const clean = cell.v.replace(/:$/, "").trim();
          if (clean) labelToAddr[clean] = { r, c, addr };
        }
      }
    }

    template.sections.forEach(sec => {
      sec.fields.forEach(field => {
        const val = report[field.id];
        if (val === undefined || val === null || val === "") return;

        const num = parseFloat(val);
        const isNum = !isNaN(num) && val.toString().trim() !== "";
        const writeVal = isNum ? num : val;
        const writeType = isNum ? "n" : "s";

        // Table fields: write by stored row/col coordinates
        if (field._tableHeaderRow !== undefined) {
          const addr = XLSX.utils.encode_cell({
            r: field._tableHeaderRow + field._tableRow + 1,
            c: field._tableCol,
          });
          const existing = ws[addr];
          ws[addr] = { ...(existing || {}), t: writeType, v: writeVal };
          delete ws[addr].f;
          return;
        }

        // Highlight-detected fields: write by stored address
        if (field._addr) {
          const existing = ws[field._addr];
          ws[field._addr] = { ...(existing || {}), t: writeType, v: writeVal };
          delete ws[field._addr].f;
          return;
        }

        // Label fields: find label, write to value cell to the right
        const labelText = field.label.trim();
        const labelInfo = labelToAddr[labelText];
        if (!labelInfo) return;

        const { r, c } = labelInfo;
        for (let vc = c + 1; vc <= Math.min(c + 8, range.e.c); vc++) {
          const vAddr = XLSX.utils.encode_cell({ r, c: vc });
          const existing = ws[vAddr];
          if (existing?.f) continue;
          ws[vAddr] = { ...(existing || {}), t: writeType, v: writeVal };
          delete ws[vAddr].f;
          break;
        }
      });
    });

    XLSX.writeFile(wb, `${(report.reportTitle || "report").replace(/\s+/g, "_")}_filled.xlsx`);
  } catch (err) {
    console.error("Clone export failed:", err);
    exportGeneric(report, template);
  }
}

// ─── Public: evaluate formula cells with current report values ────────────────
// Applies report field values to a cloned worksheet, runs evalCellFormulas,
// returns a map of { cellAddr: displayString } for all formula cells.
export function evalFormulasWithReport(template, rawBytes, report) {
  if (!rawBytes) return {};
  try {
    const wb = XLSX.read(rawBytes, { type: "array", cellFormula: true, cellStyles: true });
    const mainSheetName = (template.name && wb.Sheets[template.name]) ? template.name : wb.SheetNames[0];
    const ws = wb.Sheets[mainSheetName];
    if (!ws) return {};

    // Apply current user values to their cells
    for (const sec of template.sections || []) {
      for (const field of sec.fields || []) {
        const val = report[field.id];
        if (!field._addr || val === undefined || val === null || val === "") continue;
        const num = parseFloat(val);
        const isNum = !isNaN(num) && String(val).trim() !== "";
        ws[field._addr] = { ...(ws[field._addr] || {}), t: isNum ? "n" : "s", v: isNum ? num : val };
        if (ws[field._addr].f) delete ws[field._addr].f;
      }
    }

    // Evaluation order: main sheet first (so cross-sheet reads see fresh values),
    // then dependent sheets, then main sheet again (for cells referencing other sheets).
    evalCellFormulas(ws, wb);
    for (const name of wb.SheetNames) {
      if (name === mainSheetName) continue;
      evalCellFormulas(wb.Sheets[name], wb);
    }
    evalCellFormulas(ws, wb);

    // Build result map
    const result = {};
    for (const [addr, cell] of Object.entries(ws)) {
      if (addr.startsWith("!") || cell?.v === undefined) continue;
      if (cell.w !== undefined) result[addr] = cell.w;
      else result[addr] = String(cell.v);
    }
    return result;
  } catch { return {}; }
}

function exportGeneric(report, template) {
  const wb = XLSX.utils.book_new();
  const rows = [["Report Title", report.reportTitle || ""], ["Author", report._author || ""], ["Date", report._date || ""], []];
  template.sections.forEach(sec => { rows.push([sec.name.toUpperCase()]); sec.fields.forEach(f => rows.push([f.label, report[f.id] || ""])); rows.push([]); });
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [{ wch: 32 }, { wch: 40 }];
  XLSX.utils.book_append_sheet(wb, ws, "Report");
  XLSX.writeFile(wb, `${(report.reportTitle || "report").replace(/\s+/g, "_")}.xlsx`);
}
